/* =========================================================
   VIVID — shared section lifecycle
   VH.section(selector, init, teardown) runs `init` on every element matching
   `selector` now (this file is deferred, so the DOM is parsed) and again whenever
   the theme editor re-renders a section. The editor's event target is the
   section's wrapper <div>, so matches are looked up inside it as well.
   ========================================================= */
window.VH = {
  section(selector, init, teardown) {
    const each = (scope, fn) => {
      if (!fn || !scope) return;
      if (scope.matches && scope.matches(selector)) fn(scope);
      scope.querySelectorAll(selector).forEach(fn);
    };
    each(document, init);
    document.addEventListener('shopify:section:load', (e) => each(e.target, init));
    document.addEventListener('shopify:section:unload', (e) => each(e.target, teardown));
  }
};

// Block clicks on aria-disabled links
document.addEventListener('click', (e) => {
  const a = e.target.closest('a[aria-disabled="true"]');
  if (a) { e.preventDefault(); e.stopPropagation(); }
}, { capture: true });

/* --------------------------------------------------------
   Background videos marked [data-vh-inview] stay unloaded until they come near
   the viewport, then play while visible and pause when scrolled away.
   A fixed video (always "in view") names the element to watch instead:
   data-vh-inview=".section-vh-footer".
   -------------------------------------------------------- */
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const videoFor = new Map();
  const io = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      const video = videoFor.get(target);
      if (!video) return;
      if (isIntersecting && !reduceMotion.matches) video.play().catch(() => {});
      else if (!video.paused) video.pause();
    });
  }, { rootMargin: '200px 0px' });

  const watched = (video) => (video.dataset.vhInview && document.querySelector(video.dataset.vhInview)) || video;
  VH.section('video[data-vh-inview]', (video) => {
    const target = watched(video);
    videoFor.set(target, video);
    io.observe(target);
  }, (video) => {
    const target = watched(video);
    io.unobserve(target);
    videoFor.delete(target);
  });
})();

/* --------------------------------------------------------
   SECTION: Splash Video (vh-splash-video)
   -------------------------------------------------------- */
(function() {
  const SELECTORS = {
    video: '.vh-splash-video__el',
    toggle: '[data-js="toggle"]',
    textSpan: 'span'
  };

  /**
   * IntersectionObserver to auto-pause video when off-screen.
   * Saves CPU/Battery when user scrolls down.
   */
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const video = entry.target;
      if (!entry.isIntersecting && !video.paused) {
        video.pause();
        video.dataset.pausedByScroll = "true";
      } else if (entry.isIntersecting && video.dataset.pausedByScroll === "true") {
        // Only resume if we paused it via scroll, not if user paused it manually
        video.play().catch(() => {}); // catch interrupt errors
        delete video.dataset.pausedByScroll;
      }
    });
  }, { threshold: 0.1 });

  const init = (root) => {
    if (!root || root.getAttribute('data-section') !== 'vh-splash-video') return;

    const video = root.querySelector(SELECTORS.video);
    const toggle = root.querySelector(SELECTORS.toggle);
    
    // Safety check
    if (!video) return;

    // 1. Performance: Mark ready state to avoid FOUC
    const onMeta = () => root.setAttribute('data-ready', 'true');
    if (video.readyState >= 1) onMeta();
    else video.addEventListener('loadedmetadata', onMeta, { once: true });

    // 2. A11y & Preference: Handle Reduced Motion
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      video.removeAttribute('autoplay');
      video.pause();
    } else {
      // Observe for scroll pausing only if motion is allowed
      observer.observe(video);
    }

    // 3. Toggle Logic (Play/Pause)
    const updateUI = () => {
      if (!toggle) return;
      
      const isPlaying = !video.paused && !video.ended;
      const span = toggle.querySelector(SELECTORS.textSpan);
      
      // Update ARIA
      toggle.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      toggle.setAttribute('aria-label', isPlaying 
        ? toggle.dataset.labelPause 
        : toggle.dataset.labelPlay
      );

      // Update Text Label
      if (span) {
        span.textContent = isPlaying 
          ? (toggle.dataset.labelPause || 'Pause') 
          : (toggle.dataset.labelPlay || 'Play');
      }
    };

    const handleToggle = (e) => {
      if (e) e.preventDefault();
      if (video.paused || video.ended) {
        video.play().catch(() => {});
        delete video.dataset.pausedByScroll; // User manual override clears scroll logic
      } else {
        video.pause();
      }
    };

    // 4. Bind Events
    if (toggle) {
      toggle.addEventListener('click', handleToggle);
    }

    video.addEventListener('play', updateUI);
    video.addEventListener('pause', updateUI);
    video.addEventListener('ended', updateUI);

    // Initial Sync
    updateUI();

    // 5. Cleanup Storage (for Unload)
    root._vh_teardown = () => {
      observer.unobserve(video);
      if (toggle) toggle.removeEventListener('click', handleToggle);
      video.removeEventListener('play', updateUI);
      video.removeEventListener('pause', updateUI);
      video.removeEventListener('ended', updateUI);
    };
  };

  const teardown = (root) => {
    if (root && root._vh_teardown) {
      root._vh_teardown();
      delete root._vh_teardown;
    }
  };

  VH.section('[data-section="vh-splash-video"]', init, teardown);
})();

/**
 * VIVID Header — Consolidated Controller (V2.3 RESTORED)
 * Handles: Mobile Menu, Translucent Scroll, Main Nav Hover Pill, 
 * Locale Dropdown (Restored Logic), Cart Updates, and Layout Offsets.
 */
(function () {
  'use strict';

  // --- Utilities ---
  const qs = (s, p = document) => p.querySelector(s);
  const qsa = (s, p = document) => Array.from(p.querySelectorAll(s));
  const raf = (fn) => window.requestAnimationFrame(fn);
  const isMobile = () => window.matchMedia('(max-width: 767.98px)').matches;

  // Wraps fn so it runs at most once per frame, however often it is called
  const perFrame = (fn) => {
    let id = 0;
    const run = () => { if (!id) id = raf(() => { id = 0; fn(); }); };
    run.cancel = () => { cancelAnimationFrame(id); id = 0; };
    return run;
  };

  // --- What sits under the header: light or dark? ---
  // Things that float over the page rather than being it; skipped when looking underneath.
  const NOT_PAGE = '.vh-header, .vh-reward-popup, [data-vh-cart-drawer], .vh-reveal';

  // Computed colours come back as "rgb(10, 7, 17)", "rgba(0, 0, 0, 0)" or, for colour-mix
  // values, "color(srgb 0.1 0.05 0.2 / 0.6)". Returns [r, g, b, alpha] with r, g, b in 0–1.
  const parseColor = (str) => {
    const m = /^(rgba?|color)\((.*)\)$/.exec(String(str).trim());
    if (!m) return null;
    let parts = m[2].split(/[\s,/]+/).filter(Boolean);
    if (m[1] === 'color') parts = parts.slice(1);   // drop the colour space name
    const unit = m[1] === 'color' ? 1 : 255;
    const [r, g, b, a = 1] = parts.map(parseFloat);
    if ([r, g, b, a].some(Number.isNaN)) return null;
    return [r / unit, g / unit, b / unit, a];
  };

  // WCAG relative luminance, 0 (black) to 1 (white)
  const luminance = ([r, g, b]) => {
    const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  };

  // A section's Background setting (data-vh-tone) decides; otherwise the first solid
  // background colour behind the point does.
  const isLightAt = (x, y) => {
    const under = document.elementsFromPoint(x, y)
      .find((el) => !el.closest(NOT_PAGE) && getComputedStyle(el).position !== 'fixed');
    if (!under) return false;
    const toned = under.closest('[data-vh-tone]');
    if (toned) return toned.dataset.vhTone === 'white';
    for (let el = under; el; el = el.parentElement) {
      const c = parseColor(getComputedStyle(el).backgroundColor);
      if (c && c[3] >= 0.5) return luminance(c) > 0.6;
    }
    return false;
  };

  class VividHeader {
    constructor(root) {
      this.root = root;
      this.elements = {
        bars: qsa('.vh-bar', root),
        mobileMenu: qs('.vh-menu', root),
        toggleBtn: qs('#vh-toggle', root),
        // Where to look underneath: the left bar on desktop, the logo chip on phones
        probes: qsa('.vh-bar--left, .vh-left-pill', root),
      };

      // Configuration
      this.settings = {
        glassThreshold: 6, // px to scroll before glass effect
        glassOnScroll: root.dataset.enableGlass !== 'false', // section setting "Enable glass scroll"
        announceSel: [
          '.announcement-bar-section .utility-bar',
          '.announcement-bar-section',
          '.utility-bar'
        ].join(',')
      };

      // State
      this.state = {
        isOpen: false,
        trapFocus: null,
        observers: [],
        cleanupFns: []
      };

      this.init();
    }

    init() {
      // The announcement bar floats over the hero on "home" pages (vh-announcement.css).
      // vh-header.liquid sets this before first paint; this keeps it right after an editor re-render.
      document.documentElement.classList.toggle('vh-hero-top', this.root.dataset.heroTop === 'true');

      this.initScrollGlass();
      this.initMobileMenu();
      this.initHoverPills();
      this.initLocale();     // Restored "Nuclear Option" Logic
      this.initCartListener();
      this.initAnnouncementOffset();
      this.initTone();       // after the offset, so the bars are in place when sampled
      this.initFocusHygiene();
    }

    // addEventListener that destroy() undoes
    listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.state.cleanupFns.push(() => target.removeEventListener(type, fn, opts));
    }

    // ============================================================
    // 1. Scroll & Glass Effect
    // ============================================================
    initScrollGlass() {
      // With "Enable glass scroll" off the bars stay clear and wide while scrolling
      // (hovering still frosts them, see vh-header.css).
      const updateGlass = () => {
        const y = window.scrollY || 0;
        const isGlassy = this.settings.glassOnScroll && y > this.settings.glassThreshold;
        this.elements.bars.forEach(bar => bar.classList.toggle('is-glassy', isGlassy));
      };

      this.listen(window, 'scroll', perFrame(updateGlass), { passive: true });
      updateGlass();
    }

    // ============================================================
    // 2. Mobile Menu (Burger)
    // ============================================================
    initMobileMenu() {
      const { toggleBtn, mobileMenu } = this.elements;
      if (!toggleBtn || !mobileMenu) return;

      const toggle = (e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        this.state.isOpen ? this.closeMenu() : this.openMenu();
      };

      toggleBtn.addEventListener('click', toggle);

      qsa('a', mobileMenu).forEach(link => {
        link.addEventListener('click', () => this.closeMenu());
      });

      // Close Main Menu on Outside Click
      this.listen(document, 'click', (e) => {
        if (this.state.isOpen &&
            !mobileMenu.contains(e.target) &&
            !toggleBtn.contains(e.target)) {
          this.closeMenu();
        }
      });

      this.listen(document, 'keydown', (e) => {
        if (e.key === 'Escape' && this.state.isOpen) this.closeMenu();
      });
    }

    openMenu() {
      if (this.state.isOpen) return;
      this.state.isOpen = true;
      
      const { toggleBtn, mobileMenu } = this.elements;
      this.root.classList.add('vh-open');
      mobileMenu.classList.add('is-open');
      
      toggleBtn.setAttribute('aria-expanded', 'true');
      mobileMenu.setAttribute('aria-hidden', 'false');

      this.state.prevFocus = document.activeElement;
      const firstFocus = qs('a, button, input', mobileMenu);
      if (firstFocus) setTimeout(() => firstFocus.focus(), 50);

      this.initFocusTrap(mobileMenu);
    }

    closeMenu() {
      if (!this.state.isOpen) return;
      this.state.isOpen = false;

      const { toggleBtn, mobileMenu } = this.elements;
      this.root.classList.remove('vh-open');
      mobileMenu.classList.remove('is-open');
      
      toggleBtn.setAttribute('aria-expanded', 'false');
      mobileMenu.setAttribute('aria-hidden', 'true');

      if (this.state.prevFocus) this.state.prevFocus.focus();
      if (this.state.trapFocus) {
        document.removeEventListener('keydown', this.state.trapFocus);
        this.state.trapFocus = null;
      }

      // The page may have changed under the chips while the sheet was open
      if (this.sampleTone) this.sampleTone();
    }

    initFocusTrap(container) {
      this.state.trapFocus = (e) => {
        if (e.key !== 'Tab') return;
        const focusables = qsa('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])', container);
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      };
      document.addEventListener('keydown', this.state.trapFocus);
    }

    // ============================================================
    // 3. Traveling Hover Pill (Main Nav)
    // ============================================================
    initHoverPills() {
      const getTargets = (bar) => {
        const all = qsa('a, button, .vh-locale-chip__field', bar);
        return all.filter((el) => {
          if (el.closest('.vh-logo')) return false; 
          if (el.matches('.vh-locale-chip__chev-btn') || el.closest('.vh-locale-chip__chev-btn')) return false;
          if (el.closest('.vh-locale-menu')) return false;
          if (el.closest('.vh-shopmenu')) return false;   // the shop submenu's cards
          return el.offsetParent !== null;
        });
      };

      this.elements.bars.forEach(bar => {
        if (!qs('.vh-hover-pill', bar)) {
          const span = document.createElement('span');
          span.className = 'vh-hover-pill';
          span.setAttribute('aria-hidden', 'true');
          bar.prepend(span);
        }
      });
      
      // One pill per bar that morphs to the hovered item: it takes each item's own width and
      // springs between items (the easing lives in CSS), so it reads like a drop of liquid.
      const syncPills = () => {
        if (isMobile()) return;
        // Re-run on resize: drop the previous listeners first (kept apart from other cleanups).
        (this.state.pillCleanups || []).forEach((fn) => fn());
        this.state.pillCleanups = [];

        this.elements.bars.forEach(bar => {
          const pill = qs('.vh-hover-pill', bar);
          if (!pill) return;
          const padX = bar.classList.contains('vh-bar--right') ? 20 : 30;

          getTargets(bar).forEach(target => {
            const move = () => {
              const barRect = bar.getBoundingClientRect();
              const itemRect = target.getBoundingClientRect();
              const w = Math.round(itemRect.width + padX);
              const h = Math.round(barRect.height - 12);
              const x = (itemRect.left - barRect.left) + (itemRect.width - w) / 2;
              const y = (barRect.height - h) / 2;
              const appearing = pill.style.opacity !== '1';

              raf(() => {
                // First hover: place the pill without sliding in from the last spot.
                if (appearing) pill.classList.add('is-placing');
                pill.style.width = `${w}px`;
                pill.style.height = `${h}px`;
                pill.style.transform = `translate(${x}px, ${y}px)`;
                pill.style.opacity = '1';
                if (appearing) raf(() => pill.classList.remove('is-placing'));
              });
            };

            target.addEventListener('mouseenter', move);
            target.addEventListener('focus', move);
            this.state.pillCleanups.push(() => {
              target.removeEventListener('mouseenter', move);
              target.removeEventListener('focus', move);
            });
          });

          const hide = () => { pill.style.opacity = '0'; };
          bar.addEventListener('mouseleave', hide);
          this.state.pillCleanups.push(() => bar.removeEventListener('mouseleave', hide));
        });
      };

      syncPills();
      const ro = new ResizeObserver(() => raf(syncPills));
      this.elements.bars.forEach(b => ro.observe(b));
      this.state.observers.push(ro);
    }

    // ============================================================
    // 4. Locale Dropdown (Restored Original Logic)
    // ============================================================
    initLocale() {
      // Desktop only: the mobile menu shows languages as plain submit buttons (no JS needed).
      const instances = [
        { triggerSel: '[data-vh-locale-trigger="desktop"]', menuId: '#vh-locale-menu-desktop', formId: '#vh-locale-desktop-form', mode: 'hover' }
      ];

      instances.forEach(config => {
        const trigger = qs(config.triggerSel, this.root);
        const menu = qs(config.menuId, this.root);
        const form = qs(config.formId, this.root);
        
        if (!trigger || !menu || !form) return;

        const btns = qsa('.vh-locale-menu__btn', menu);
        let closeTimer;

        // --- CORE LOGIC (Restored from your V1) ---
        const open = () => {
          clearTimeout(closeTimer);
          menu.classList.add('is-open');
        };

        const close = () => {
          closeTimer = setTimeout(() => menu.classList.remove('is-open'), 150);
        };

        const toggle = (e) => {
            // THE NUCLEAR OPTION (Restored)
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            if (menu.classList.contains('is-open')) {
                menu.classList.remove('is-open');
            } else {
                // Close other open menus first
                qsa('.vh-locale-menu.is-open').forEach(m => m.classList.remove('is-open'));
                open();
            }
        };

        // --- BINDING ---
        if (config.mode === 'hover') {
            trigger.addEventListener('mouseenter', open);
            trigger.addEventListener('mouseleave', close);
            menu.addEventListener('mouseenter', open);
            menu.addEventListener('mouseleave', close);
        } else {
            // Mobile: Click (Isolated)
            const actualClickTarget = trigger.querySelector('.vh-locale-chip') || trigger;
            actualClickTarget.addEventListener('click', toggle);
        }

        // Button Logic
        btns.forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const input = qs('input[name="locale_code"]', form);
            if (input) input.value = btn.value;
            form.submit();
          });
        });
      });
      
      // Global "Click Outside" to close dropdowns
      // (This only fires if stopPropagation wasn't called)
      this.listen(document, 'click', (e) => {
         qsa('.vh-locale-menu.is-open').forEach(menu => {
             const id = menu.id;
             const isDesktop = id.includes('desktop');
             const triggerAttr = isDesktop ? 'desktop' : 'mobile';
             const trigger = qs(`[data-vh-locale-trigger="${triggerAttr}"]`);
             
             if (!menu.contains(e.target) && !trigger.contains(e.target)) {
                 menu.classList.remove('is-open');
             }
         });
      });
    }
    
    // ============================================================
    // 5. Cart Listener & Announcement Offset
    // ============================================================
    initCartListener() {
      const updateCart = (e) => {
        const count = e.detail?.count ?? e.detail?.cart?.item_count;
        if (count === undefined) return;

        this.root.setAttribute('data-cart-count', count);
        const badges = qsa('.vh-cart-badge', this.root);
        badges.forEach(b => {
            b.textContent = count > 99 ? '99+' : count;
            b.hidden = count === 0;
        });
      };
      
      window.addEventListener('cart:updated', updateCart);
      window.addEventListener('vh:cart-count', updateCart);
      this.state.cleanupFns.push(() => {
        window.removeEventListener('cart:updated', updateCart);
        window.removeEventListener('vh:cart-count', updateCart);
      });
    }

    // The header sits just under the announcement bar and rises with it as it scrolls away.
    // The bar is at the very top of the page whether it floats over the hero or not, so its
    // height minus the scroll is how much of it is still on screen.
    initAnnouncementOffset() {
      const getBar = () => qs(this.settings.announceSel);

      const measure = () => {
        const bar = getBar();
        const h = bar ? bar.getBoundingClientRect().height : 0;
        this.announcementHeight = h;
        onScroll();
      };

      const onScroll = () => {
        const y = window.scrollY || 0;
        const h = this.announcementHeight || 0;
        const safeY = Math.max(0, y);
        const newOffset = Math.max(0, h - safeY);
        this.root.style.setProperty('--vh-announcement-offset', `${newOffset}px`);
      };

      const bar = getBar();
      if (bar && window.ResizeObserver) {
        const ro = new ResizeObserver(perFrame(measure));
        ro.observe(bar);
        this.state.observers.push(ro);
      }

      this.listen(window, 'resize', perFrame(measure), { passive: true });
      this.listen(window, 'scroll', perFrame(onScroll), { passive: true });

      measure();
    }

    // ============================================================
    // 5b. Light or dark underneath: .is-on-light flips the header's ink and glass
    // ============================================================
    initTone() {
      const sample = () => {
        if (this.state.isOpen) return;   // the open sheet is dark glass whatever is behind it
        const probe = this.elements.probes.find((el) => el.getClientRects().length);
        if (!probe) return;
        const r = probe.getBoundingClientRect();
        this.root.classList.toggle('is-on-light', isLightAt(r.left + r.width / 2, r.top + r.height / 2));
      };

      this.sampleTone = perFrame(sample);
      this.listen(window, 'scroll', this.sampleTone, { passive: true });
      this.listen(window, 'resize', this.sampleTone, { passive: true });
      this.listen(window, 'load', this.sampleTone);
      this.listen(document, 'shopify:section:load', this.sampleTone);
      this.state.cleanupFns.push(() => this.sampleTone.cancel());
      sample();
    }
    
    // ============================================================
    // 6. Focus Hygiene
    // ============================================================
    initFocusHygiene() {
      let isPointer = false;
      const setPointer = () => { isPointer = true; };
      ['mousedown', 'touchstart'].forEach(ev => 
          this.root.addEventListener(ev, setPointer, { passive: true })
      );

      this.root.addEventListener('focusin', (e) => {
          if (isPointer) {
            const el = e.target;
            if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') {
                el.blur();
            }
            isPointer = false;
          }
      });
    }

    destroy() {
        this.state.observers.forEach(o => o.disconnect());
        this.state.cleanupFns.forEach(fn => fn());
        (this.state.pillCleanups || []).forEach(fn => fn());
        if (this.state.trapFocus) document.removeEventListener('keydown', this.state.trapFocus);
    }
  }

  // ============================================================
  // Bootstrapping
  // ============================================================
  const map = new WeakMap();

  const load = (root) => {
    if (map.has(root)) return;
    const instance = new VividHeader(root);
    map.set(root, instance);
  };

  const unload = (root) => {
    if (!map.has(root)) return;
    map.get(root).destroy();
    map.delete(root);
  };

  VH.section('.vh-header', load, unload);

})();

/* =========================================================
   VIVID — V1 Product Display (Controller)
   Standard: v2.0
   Scope: [data-section="v1-product-display"]
   ========================================================= */

(function () {
  'use strict';

  const SELECTOR = '[data-section="v1-product-display"]';
  const REGISTRY = new WeakMap();

  class ProductDisplay {
    constructor(root) {
      this.root = root;
      this.cards = root.querySelectorAll('.vh-prod-card');
      this.grid = root.querySelector('.vh-products__grid');
      this.dots = root.querySelectorAll('.vh-products__dot');
      
      // State & Observers
      this.observers = [];
      this.cleanupFns = [];
      this.isRTL = document.documentElement.getAttribute('dir') === 'rtl';

      this.init();
    }

    init() {
      if (!this.cards.length) return;

      this.setupCardClicks();
      this.setupVideoPerformance();
      this.setupMobileNavigation();
    }

    /**
     * 1. Clickable Card Pattern
     * Makes the entire card clickable if a primary CTA exists,
     * but ignores clicks on other interactive elements.
     */
    setupCardClicks() {
      this.cards.forEach((card) => {
        const cta = card.querySelector('.vh-cta');
        // Abort if no valid CTA found or it's disabled
        if (!cta || cta.getAttribute('aria-disabled') === 'true') {
          return;
        }

        const handleClick = (e) => {
          // 1. Allow text selection
          if (window.getSelection().toString().length > 0) return;

          // 2. Ignore clicks on nested interactive elements
          if (e.target.closest('a, button, input, label, select, textarea') && e.target !== cta) {
            return;
          }

          // 3. Prevent infinite loop if clicking the CTA itself
          if (e.target === cta) return;

          // 4. Trigger the click
          e.preventDefault();
          cta.click();
        };

        card.style.cursor = 'pointer';
        card.addEventListener('click', handleClick);
        this.cleanupFns.push(() => {
          card.removeEventListener('click', handleClick);
          card.style.cursor = '';
        });
      });
    }

    /**
     * 2. Video Performance (Updated)
     * - Desktop: Play on Hover, Pause on Leave.
     * - Mobile: Play when scrolled into view (Autoplay behavior).
     * - Global: Pause when scrolled out of view.
     */
    setupVideoPerformance() {
      const videos = this.root.querySelectorAll('video');
      if (!videos.length) return;

      const isMobile = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // A. Desktop Hover Logic
      if (!isMobile && !prefersReduced) {
        videos.forEach((video) => {
          const card = video.closest('.vh-prod-card');
          if (!card) return;

          const onEnter = () => video.play().catch(() => {});
          const onLeave = () => {
            video.pause();
            video.currentTime = 0; // Optional: Reset to start
          };

          card.addEventListener('mouseenter', onEnter);
          card.addEventListener('mouseleave', onLeave);

          // Cleanup
          this.cleanupFns.push(() => {
            card.removeEventListener('mouseenter', onEnter);
            card.removeEventListener('mouseleave', onLeave);
          });
        });
      }

      // B. Scroll Visibility Logic (Mobile Autoplay + Global Optimize)
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          
          if (!entry.isIntersecting) {
            // Always pause if off screen
            video.pause();
          } else if (isMobile && !prefersReduced) {
            // Mobile: Simulate "Autoplay" when visible
            video.play().catch(() => {});
          }
        });
      }, { threshold: 0.5 }); // 50% visible before playing on mobile

      videos.forEach((video) => observer.observe(video));
      this.observers.push(observer);

      // C. Poster fallback: the card normally rests on the video's first frame; if the browser
      //    refuses to preload it (data saver, iOS Low Power Mode), show the still image instead.
      videos.forEach((video) => {
        const poster = video.dataset.poster;
        if (!poster) return;
        const usePoster = () => { if (video.readyState < 2) video.poster = poster; };
        const timer = setTimeout(usePoster, 2500);
        video.addEventListener('loadeddata', () => clearTimeout(timer), { once: true });
        video.addEventListener('error', usePoster, true);
        this.cleanupFns.push(() => clearTimeout(timer));
      });
    }

    /**
     * 3. Mobile Navigation (Dots & Scroll Sync)
     * Handles the scroll snapping logic and active dot state.
     * Includes RTL support and resize listeners.
     */
    setupMobileNavigation() {
      if (!this.grid || !this.dots.length) return;

      const mediaQuery = window.matchMedia('(max-width: 768px)');
      
      const handleScroll = () => {
        // Debounce slightly for performance
        if (this.scrollTicking) return;
        this.scrollTicking = true;
        
        requestAnimationFrame(() => {
          this.updateActiveDot();
          this.scrollTicking = false;
        });
      };

      const mountMobile = () => {
        this.grid.addEventListener('scroll', handleScroll, { passive: true });
        this.updateActiveDot(); // Initial check
      };

      const unmountMobile = () => {
        this.grid.removeEventListener('scroll', handleScroll);
      };

      // Listener for screen resize (Mount/Unmount logic)
      const handleResize = (e) => {
        if (e.matches) mountMobile();
        else unmountMobile();
      };

      // Initial Mount
      if (mediaQuery.matches) mountMobile();
      
      // Bind Listener
      mediaQuery.addEventListener('change', handleResize);
      this.cleanupFns.push(() => mediaQuery.removeEventListener('change', handleResize));
      
      // Cleanup Scroll Listener explicitly on destroy
      this.cleanupFns.push(unmountMobile);

      // Bind Dot Clicks (Always active, just hidden on desktop via CSS)
      this.dots.forEach((dot, index) => {
        const onDotClick = () => {
          const card = this.cards[index];
          if (!card) return;

          // RTL Aware Scroll Calculation
          const gridWidth = this.grid.clientWidth;
          const cardWidth = card.clientWidth;
          const cardLeft = card.offsetLeft;
          
          let targetPos;

          if (this.isRTL) {
             // In RTL, scrollLeft is negative or inverted depending on browser engine
             // Safest to use scrollIntoView for RTL complexity
             card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          } else {
             targetPos = cardLeft - (gridWidth / 2) + (cardWidth / 2);
             this.grid.scrollTo({ left: targetPos, behavior: 'smooth' });
          }
        };

        dot.addEventListener('click', onDotClick);
        this.cleanupFns.push(() => dot.removeEventListener('click', onDotClick));
      });
    }

    updateActiveDot() {
      const center = this.grid.scrollLeft + (this.grid.clientWidth / 2);
      let closestIndex = 0;
      let minDistance = Infinity;

      this.cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + (card.clientWidth / 2);
        const distance = Math.abs(center - cardCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      this.dots.forEach((dot, index) => {
        if (index === closestIndex) {
          dot.classList.add('is-active');
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove('is-active');
          dot.removeAttribute('aria-current');
        }
      });
    }

    destroy() {
      // 1. Disconnect Observers
      this.observers.forEach((obs) => obs.disconnect());
      
      // 2. Run Cleanup Functions (Listeners)
      this.cleanupFns.forEach((fn) => fn());
      
      // 3. Reset State
      this.observers = [];
      this.cleanupFns = [];
    }
  }

  // =========================================================
  // Lifecycle Management (Shopify Theme Editor Safe)
  // =========================================================

  const init = (root) => {
    if (!root || REGISTRY.has(root)) return;
    REGISTRY.set(root, new ProductDisplay(root));
  };

  const teardown = (root) => {
    const instance = REGISTRY.get(root);
    if (instance) {
      instance.destroy();
      REGISTRY.delete(root);
    }
  };

  VH.section(SELECTOR, init, teardown);

})();

/* === VH — Capsule Tech (Fix: Mobile Spacing & No-Double Dots) === */
(function() {
  const SECTION_KEY = 'vh-capsule-tech';
  const SELECTOR = `[data-section="${SECTION_KEY}"]`;

  function init(root) {
    if (!root || root._vhCtInited) return;
    root._vhCtInited = true;

    const card = root.querySelector('.vh-capsule-tech__card');
    const mediaCol = root.querySelector('.vh-capsule-tech__right');
    const videoWrap = root.querySelector('.vh-ct-video-wrap');
    const videoEl = root.querySelector('.vh-ct-video');
    const callouts = Array.from(root.querySelectorAll('.vh-ct-callout'));
    const anchors = Array.from(root.querySelectorAll('.vh-ct-anchor'));
    
    // Environment
    const mqlMobile = window.matchMedia('(max-width: 767px)');
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = root.dataset.autoplay === 'true';

    // 1. Setup SVG
    const SVG_NS = 'http://www.w3.org/2000/svg';
    let wires = root.querySelector('.vh-ct-wires-container svg');
    if (!wires) {
      const container = root.querySelector('.vh-ct-wires-container');
      if (container) {
        wires = document.createElementNS(SVG_NS, 'svg');
        wires.setAttribute('aria-hidden', 'true');
        wires.style.width = '100%';
        wires.style.height = '100%';
        container.appendChild(wires);
      }
    }

    // 2. Map Anchors
    const anchorMap = {};
    anchors.forEach(a => {
      const key = Array.from(a.classList).find(c => c.startsWith('vh-ct-anchor--'));
      if (key) anchorMap['.' + key] = a;
    });

    // 3. Layout Logic
    function layoutCallouts() {
      if (!mediaCol || !wires) return;

      const mediaRect = mediaCol.getBoundingClientRect();
      const mobile = mqlMobile.matches;

      const K = {
        BASE_SPREAD: mobile ? 0 : Math.min(140, mediaRect.height * 0.22),
        DOT_RADIUS: 3,
        JITTER_MAX: (prefersReduced || mobile) ? 0 : 36
      };

      // Clear SVG
      while (wires.firstChild) wires.removeChild(wires.firstChild);
      
      const plans = [];
      
      // A. Calculate Positions
      if (mobile) {
        // FIXED: Reduced start gap and item gap for tighter mobile feel
        let cursorY = 0; 
        const gap = 12; // Tighter gap (was 16)
        
        callouts.forEach(co => {
          const h = co.getBoundingClientRect().height || 40;
          const topPx = cursorY;
          cursorY = topPx + h + gap;
          plans.push({ co, topPx, mobile: true });
        });
        // The column grows to fit the stacked callouts instead of overlapping them at the bottom
        mediaCol.style.minHeight = `${Math.ceil(cursorY - gap)}px`;
      } else {
        mediaCol.style.minHeight = '';
        callouts.forEach((co, i) => {
          const anchorSel = co.getAttribute('data-anchor');
          const anchor = anchorMap[anchorSel];
          if (!anchor) return;

          const aRect = anchor.getBoundingClientRect();
          const coRect = co.getBoundingClientRect();
          
          let topPx = (aRect.top + aRect.height/2) - mediaRect.top - (coRect.height/2);
          
          const variant = co.getAttribute('data-variant');
          const offset = parseFloat(co.getAttribute('data-offset')) || 0;
          const jitter = Math.sin((i+1) * 1.7) * K.JITTER_MAX;

          if (variant === 'top') topPx -= (K.BASE_SPREAD + jitter);
          if (variant === 'bottom') topPx += (K.BASE_SPREAD + jitter);
          
          topPx += offset;
          topPx = Math.max(0, Math.min(mediaRect.height - coRect.height, topPx));
          
          plans.push({ co, anchor, topPx, mobile: false, side: co.getAttribute('data-side') });
        });
      }

      // B. Apply Positions & Draw
      plans.forEach(p => {
        p.co.style.top = `${p.topPx}px`;
        
        const coRect = p.co.getBoundingClientRect();
        const startY = (coRect.top + coRect.height/2) - mediaRect.top;
        
        if (p.mobile) {
          // FIXED: Mobile Wire starts at RIGHT edge of text, not left.
          const videoRect = videoWrap ? videoWrap.getBoundingClientRect() : null;
          if (!videoRect) return;
          
          const startX = (coRect.right - mediaRect.left) + 8; // Start 8px after text ends
          // The capsule sits in the middle of a transparent frame: end the wire just short of it
          const endX = (videoRect.left - mediaRect.left) + videoRect.width * 0.26;
          
          if (endX > startX) {
            // Draw Line
            const path = document.createElementNS(SVG_NS, 'path');
            path.setAttribute('d', `M ${startX} ${startY} H ${endX}`);
            wires.appendChild(path);
            
            // Draw Dot at Video End
            const circle = document.createElementNS(SVG_NS, 'circle');
            circle.setAttribute('cx', endX);
            circle.setAttribute('cy', startY);
            circle.setAttribute('r', K.DOT_RADIUS);
            wires.appendChild(circle);
          }
          
        } else {
          // Desktop Wire (Elbow)
          const aRect = p.anchor.getBoundingClientRect();
          const endY = (aRect.top + aRect.height/2) - mediaRect.top;
          const side = p.side;
          
          const padX = side === 'left' 
            ? parseFloat(getComputedStyle(p.co).paddingRight) 
            : parseFloat(getComputedStyle(p.co).paddingLeft);
            
          const startX = side === 'left'
            ? (coRect.right - padX - mediaRect.left)
            : (coRect.left + padX - mediaRect.left);
            
          const endX = side === 'left'
            ? (aRect.left - mediaRect.left) + K.DOT_RADIUS
            : (aRect.right - mediaRect.left) - K.DOT_RADIUS;

          const midX = startX + (endX - startX) * 0.5;
          const d = `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
          
          const path = document.createElementNS(SVG_NS, 'path');
          path.setAttribute('d', d);
          wires.appendChild(path);

          const circle = document.createElementNS(SVG_NS, 'circle');
          circle.setAttribute('cx', endX);
          circle.setAttribute('cy', endY);
          circle.setAttribute('r', K.DOT_RADIUS);
          wires.appendChild(circle);
        }
      });
      
      // C. Mobile Video Vertical Alignment
      if (mobile && videoWrap && plans.length > 1) {
        const first = plans[0].topPx;
        const last = plans[plans.length-1].topPx + 40; 
        const mid = (first + last) / 2;
        const vH = videoWrap.offsetHeight;
        videoWrap.style.marginTop = `${Math.max(0, mid - vH/2)}px`;
      } else {
        videoWrap.style.marginTop = '';
      }
    }

    // 4. Observers
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          card.classList.add('is-visible');
          if (videoEl && autoplay) videoEl.play().catch(()=>{});
          requestAnimationFrame(layoutCallouts);
        } else {
          if (videoEl) videoEl.pause();
        }
      });
    }, { threshold: 0.2 });
    observer.observe(root);
    root._vhIO = observer;

    const resizeObs = new ResizeObserver(() => requestAnimationFrame(layoutCallouts));
    if (mediaCol) resizeObs.observe(mediaCol);
    root._vhRO = resizeObs;

    requestAnimationFrame(layoutCallouts);
  }

  const unload = (root) => {
    if (root._vhIO) root._vhIO.disconnect();
    if (root._vhRO) root._vhRO.disconnect();
    root._vhCtInited = false;
  };

  VH.section(SELECTOR, init, unload);
})();

/* ==========================================
   Section: VH — Science Splash (scoped) (.vh-science-splash)
   ========================================== */

(function () {
  'use strict';

  // ------------------------------------------------------------
  // Utils
  // ------------------------------------------------------------
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var raf = function (fn) { return requestAnimationFrame(fn); };

  // ------------------------------------------------------------
  // Shared: Inert disabled anchors (.vh-cta.is-disabled)
  // ------------------------------------------------------------
  function inertAnchors(root) {
    var scope = root || document;
    scope.querySelectorAll('.vh-cta.is-disabled').forEach(function (el) {
      if (el.dataset._inert) return;
      el.dataset._inert = '1';
      el.removeAttribute('href');
      el.setAttribute('aria-disabled', 'true');
      el.tabIndex = -1;

      var stop = function (e) { e.preventDefault(); e.stopPropagation(); };
      el.addEventListener('click', stop, { passive: false });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') stop(e);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { inertAnchors(document); }, { once: true });
  } else {
    inertAnchors(document);
  }

  // ------------------------------------------------------------
  // Section module: vh-science-splash — init/teardown
  // ------------------------------------------------------------
  function initSplash(sectionEl) {
    var destroyFns = [];
    var observed = [];

    function applyInview(targets) {
      raf(function () {
        targets.forEach(function (el) { el.classList.add('is-inview'); });
      });
    }

    var targets = [sectionEl].concat(Array.from(
      sectionEl.querySelectorAll('.vh-science-splash__media-inner, .vh-science-splash__title, .vh-science-splash__sub')
    ));

    if (prefersReducedMotion) {
      applyInview(targets);
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            applyInview([entry.target]);
            io.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.2 });

      targets.forEach(function (t) { io.observe(t); observed.push(t); });
      destroyFns.push(function () { io.disconnect(); observed = []; });
    }

    // Ensure disabled CTAs inside this section are inert
    inertAnchors(sectionEl);

    // Return teardown
    return function teardown() {
      destroyFns.forEach(function (fn) { try { fn(); } catch (e) {} });
      destroyFns = [];
    };
  }

  // ------------------------------------------------------------
  // Bootstrap instances + Shopify Theme Editor lifecycle
  // ------------------------------------------------------------
  var instances = new WeakMap();

  function mount(el) {
    if (instances.has(el)) return;
    instances.set(el, initSplash(el) || function () {});
  }

  function unmount(el) {
    var teardown = instances.get(el);
    if (teardown) {
      try { teardown(); } catch (e) {}
      instances.delete(el);
    }
  }

  VH.section('.vh-science-splash', mount, unmount);
  document.addEventListener('shopify:section:load', function (e) { inertAnchors(e.target); });
})();

/* =======================================================================
   VIVID — PANELS (vh-duo-panels): photos fade in once decoded
   ======================================================================= */

(function() {
  const init = (root) => {
    if (!root) return;
    
    // Smooth image loading implementation
    const images = root.querySelectorAll('img.vh-image-smooth');
    images.forEach(img => {
      if (img.complete) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
        img.addEventListener('error', () => img.classList.add('is-loaded'), { once: true }); // Fail gracefully
      }
    });
  };

  VH.section('.vh-duo-panels', init);
})();

/* =======================================================================
   VIVID — PDP (FINAL) JS — CLEANED
   Scope: sections/vh-product.liquid  (root: [data-vh="vh-product"])
   ======================================================================= */
(() => {
  const ROOT_SELECTOR = '[data-vh="vh-product"]';
  const INSTANCES = new WeakMap();

  /* ----------------- tiny helpers ----------------- */
  const $  = (root, s) => root.querySelector(s);
  const $$ = (root, s) => Array.from(root.querySelectorAll(s));
  const on = (el, type, fn, opts) => { el && el.addEventListener(type, fn, opts); return () => el && el.removeEventListener(type, fn, opts); };
  const raf = (fn) => requestAnimationFrame(fn);

  /* ----------------- sticky offset under header ----------------- */
  function getHeaderHeight() {
    const candidates = [
      '#shopify-section-header',
      '.shopify-section-header',
      'header.header',
      'header.site-header',
      '.sticky-header',
      '[data-sticky-header]',
      '[data-header]'
    ];

    let maxH = 0;
    candidates.forEach(sel => {
      const el = document.querySelector(sel);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      maxH = Math.max(maxH, rect.height || el.offsetHeight || 0);
    });
    return maxH;
  }

  function setStickyTop(root) {
    const top = Math.max(8, Math.round(getHeaderHeight()) + 8);
    root.style.setProperty('--stickyTop', `${top}px`);
  }

  /* ----------------- accordions ----------------- */
  function initAccordions(root, bag) {
    const wrap = root.querySelector('[data-accordion]');
    if (!wrap) return;

    $$(wrap, '.vh-acc__item').forEach(item => {
      const btn = item.querySelector('.vh-acc__t');
      const panel = item.querySelector('.vh-acc__p');
      if (!btn || !panel) return;

      // default closed
      btn.setAttribute('aria-expanded', 'false');
      panel.hidden = true;

      let lock = false;
      const toggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        if (lock) return;
        lock = true;

        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;

        requestAnimationFrame(() => { lock = false; });
      };


      bag.cleanups.push(on(btn, 'click', toggle));
      // keyboard coverage if markup ever morphs away from <button>
      bag.cleanups.push(on(btn, 'keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); btn.click(); }
      }));
    });
  }

  /* ----------------- packs + quantity sync ----------------- */
  // Picking a pack sets the quantity; changing the quantity selects the pack whose
  // discount tier it reaches (5–9 boxes → bundle 1, 10+ → bundle 2). The optional
  // "Try it first" pack swaps the variant the form adds and keeps its own quantity.
  function initControls(root, bag) {
    const packs      = $$(root, '.vh-pack__input');
    const boxPacks   = packs.filter((p) => !p.hasAttribute('data-trial'));
    const qtyInput   = root.querySelector('.quantity__input');
    const minusBtn   = root.querySelector('.quantity__button[name="minus"]');
    const plusBtn    = root.querySelector('.quantity__button[name="plus"]');
    const bundleProp = root.querySelector('[data-bundle-prop]');
    const variantIn  = root.querySelector('.product-variant-id');
    // From Liquid, not the live input: a reload can restore the trial id into it
    const boxVariant = root.dataset.boxVariant || (variantIn ? variantIn.value : '');
    const trialImg   = root.querySelector('[data-trial-image]');

    // The "Try it first" photo crossfades over the main one; it downloads on first use
    const showTrialImage = (on) => {
      if (!trialImg) return;
      if (on && !trialImg.getAttribute('src')) {
        trialImg.srcset = trialImg.dataset.srcset;
        trialImg.src = trialImg.dataset.src;
      }
      trialImg.hidden = false;
      requestAnimationFrame(() => root.classList.toggle('is-trial', on));
      if (on) root.querySelector('.vh-mosaic')?.scrollTo({ left: 0, behavior: 'smooth' });
    };

    const setQty = (n) => {
      if (!qtyInput) return;
      qtyInput.value = String(Math.max(1, Number(n) || 1));
      // fire both to satisfy any theme listeners
      qtyInput.dispatchEvent(new Event('input',  { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const packForQty = (qty) => {
      let match = boxPacks[0] || null;
      boxPacks.forEach((p) => { if (qty >= Number(p.value)) match = p; });
      return match;
    };

    function syncFromQty() {
      const qty = Number(qtyInput?.value || 1);
      const trial = packs.find((p) => p.checked && p.hasAttribute('data-trial'));
      const pack = trial || packForQty(qty);
      if (pack) pack.checked = true;
      if (bundleProp) bundleProp.value = !trial && pack && Number(pack.value) > 1 ? pack.dataset.bundle : '';
      root.dispatchEvent(new CustomEvent('vh:pack-change', { detail: { qty, pack } }));
    }

    packs.forEach((pack) => {
      bag.cleanups.push(on(pack, 'change', () => {
        if (!pack.checked) return;
        if (variantIn) variantIn.value = pack.dataset.variant || boxVariant;
        showTrialImage(pack.hasAttribute('data-trial'));
        setQty(pack.value);
      }));
    });

    if (qtyInput) {
      bag.cleanups.push(on(qtyInput, 'input', syncFromQty));
      bag.cleanups.push(on(qtyInput, 'change', syncFromQty));
    }
    if (minusBtn) bag.cleanups.push(on(minusBtn, 'click', () => setTimeout(syncFromQty, 0)));
    if (plusBtn)  bag.cleanups.push(on(plusBtn,  'click', () => setTimeout(syncFromQty, 0)));

    // The browser may have restored a picked pack (reload, back): make the form and photo match it
    const restored = packs.find((p) => p.checked);
    if (variantIn) variantIn.value = (restored && restored.dataset.variant) || boxVariant;
    if (restored && restored.hasAttribute('data-trial')) showTrialImage(true);

    syncFromQty();
  }

  /* ----------------- sticky buy bar (desktop pill, phone sheet) ----------------- */
  // Appears once the main Add to cart row has scrolled above the screen; its button
  // simply presses the real one, so quantity, packs, errors and the cart drawer all
  // behave the same. CSS hides it while the cart drawer is open (html.vh-drawer-open).
  function initStickyBar(root, bag) {
    const bar = root.querySelector('[data-stickybuy]');
    const row = root.querySelector('[data-buyrow]');
    const submit = root.querySelector('.product-form__submit');
    if (!bar || !row || !submit) return;

    const btn = bar.querySelector('[data-stickybuy-btn]');
    const packOut = bar.querySelector('[data-stickybuy-pack]');
    const price = bar.querySelector('[data-stickybuy-price]');
    const unitPrice = price ? price.textContent.trim() : '';
    // "1 box" / "[n] boxes", already translated by the section
    const boxes = (n) => (n === 1 ? bar.dataset.labelOne : (bar.dataset.labelMany || '[n]').replace('[n]', n)) || String(n);

    // Shown while the buy row is above the screen and the footer isn't up yet
    const footer = document.querySelector('.section-vh-footer');
    let passed = false;
    let atFooter = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === row) passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        else atFooter = entry.isIntersecting;
      });
      const show = passed && !atFooter;
      bar.classList.toggle('is-shown', show);
      bar.inert = !show;
    });
    io.observe(row);
    if (footer) io.observe(footer);
    bag.cleanups.push(() => io.disconnect());

    // Follows the pack picker: the pack's name and total, or "7 boxes" and "7 × €25,00"
    // for a quantity between the packs (the cart works the discount out itself)
    bag.cleanups.push(on(root, 'vh:pack-change', (e) => {
      const { qty, pack } = e.detail;
      const exact = !!pack && Number(pack.value) === qty;
      const trial = !!pack && pack.hasAttribute('data-trial');
      const unit = (pack && pack.dataset.unit) || unitPrice;
      if (packOut) packOut.textContent = exact || trial ? pack.dataset.name : boxes(qty);
      if (price) price.textContent = exact ? pack.dataset.total : (qty === 1 ? unit : `${qty} × ${unit}`);
    }));

    // Mirrors the real button: spinner while an add is in flight (aria-disabled, stays
    // focusable), disabled only when the real one is (sold out, unavailable)
    const mirror = () => {
      const busy = submit.classList.contains('loading');
      btn.classList.toggle('is-loading', busy);
      btn.setAttribute('aria-busy', String(busy));
      if (busy) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
      btn.disabled = !busy && (submit.disabled || submit.getAttribute('aria-disabled') === 'true');
    };
    const mo = new MutationObserver(mirror);
    mo.observe(submit, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
    bag.cleanups.push(() => mo.disconnect());
    mirror();

    // An add started here that fails (e.g. not enough stock) shows its message next to the
    // main button, above the screen: bring that message into view
    let fromBar = false;
    const err = root.querySelector('.product-form__error-message-wrapper');
    if (err) {
      const eo = new MutationObserver(() => {
        if (!fromBar || err.hidden) return;
        fromBar = false;
        row.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        err.tabIndex = -1;
        err.focus({ preventScroll: true });
      });
      eo.observe(err, { attributes: true, attributeFilter: ['hidden'] });
      bag.cleanups.push(() => eo.disconnect());
    }

    bag.cleanups.push(on(btn, 'click', () => {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      fromBar = true;
      setTimeout(() => { fromBar = false; }, 8000);   // only this add's answer counts
      submit.click();
    }));
  }

  /* ----------------- delivery estimate ----------------- */
  // "Order within 3 h 12 min — arrives Tue 4 – Thu 6 Nov". Business days run Monday to
  // Friday minus the days off; an order before the cut-off (in the shop's time zone) ships
  // that day. The arithmetic runs on calendar days held as UTC midnights and is printed
  // in UTC, so the visitor's own time zone never shifts a date.
  const DAY = 864e5;
  const isoDay = (t) => new Date(t).toISOString().slice(0, 10);

  // Wall-clock time in a time zone: the calendar day (UTC midnight) and minutes past midnight
  function zonedClock(now, tz) {
    let parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23',
        year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
      }).formatToParts(now);
    } catch (e) { return null; }
    const get = (type) => Number((parts.find((p) => p.type === type) || {}).value);
    const clock = {
      day: Date.UTC(get('year'), get('month') - 1, get('day')),
      minutes: (get('hour') % 24) * 60 + get('minute') + get('second') / 60
    };
    return Number.isFinite(clock.day) && Number.isFinite(clock.minutes) ? clock : null;
  }

  // Pure: { left: minutes to today's cut-off (0 = passed / not a working day), from, to }
  function deliveryEstimate({ now, tz, cutoff, min, max, holidays }) {
    const clock = zonedClock(now, tz) || zonedClock(now, 'Europe/Madrid');
    if (!clock) return null;
    const open = (t) => {
      const wd = new Date(t).getUTCDay();
      return wd !== 0 && wd !== 6 && !holidays.has(isoDay(t));
    };
    const after = (t, n) => {
      let d = t;
      for (let guard = 0; n > 0 && guard < 400; guard++) { d += DAY; if (open(d)) n--; }
      return d;
    };
    const cut = cutoff * 60;
    const today = open(clock.day) && clock.minutes < cut;
    const ship = today ? clock.day : after(clock.day, 1);
    const lo = Math.min(min, max);
    const hi = Math.max(min, max);
    return { left: today ? Math.ceil(cut - clock.minutes) : 0, from: after(ship, lo), to: after(ship, hi) };
  }

  function initDelivery(root, bag) {
    const el = root.querySelector('[data-vh-delivery]');
    const out = el && el.querySelector('[data-vh-delivery-text]');
    if (!el || !out) return;
    const d = el.dataset;

    // Which range: home country, the EU list, or everyone else (only if switched on)
    const country = (d.country || '').toUpperCase();
    const days = (s) => {
      const [a, b] = String(s || '').split(',').map((v) => (v.trim() === '' ? NaN : Number(v)));
      return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
    };
    const eu = (d.eu || '').toUpperCase().split(/[\s,;]+/).filter(Boolean);
    let range;
    if (country && country === (d.home || 'ES')) range = days(d.homeDays);
    else if (country && eu.includes(country)) range = days(d.euDays);
    else range = days(d.otherDays);
    if (!range) return;   // no estimate for this country: the line stays hidden

    const holidays = new Set((d.holidays || '').match(/\d{4}-\d{2}-\d{2}/g) || []);

    // Dates in the page language, shaped for the visitor's country where the browser knows
    // it (English outside the US reads "Tue 4 – Thu 6 Nov")
    const lang = d.locale || document.documentElement.lang || 'en';
    const pickLocale = () => {
      const tries = [];
      if (!lang.includes('-') && country) tries.push(`${lang}-${country}`);
      if (lang.toLowerCase() === 'en' && country !== 'US') tries.push('en-150');
      tries.push(lang);
      for (const tag of tries) {
        try {
          if (new Intl.DateTimeFormat(tag).resolvedOptions().locale.toLowerCase() === tag.toLowerCase()) return tag;
        } catch (e) { /* not a valid tag here: try the next */ }
      }
      return undefined;
    };
    const opts = { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' };
    let fmt;
    try { fmt = new Intl.DateTimeFormat(pickLocale(), opts); } catch (e) { fmt = new Intl.DateTimeFormat(undefined, opts); }
    const span = (a, b) => {
      if (a === b) return fmt.format(new Date(a));
      try { return fmt.formatRange(new Date(a), new Date(b)); } catch (e) { return `${fmt.format(new Date(a))} – ${fmt.format(new Date(b))}`; }
    };

    // Fills a text with [time] / [dates] tokens; the two values get their own spans
    const fill = (tpl, values) => {
      out.textContent = '';
      String(tpl || '').split(/(\[time\]|\[dates\])/).forEach((part) => {
        if (!part) return;
        if (!(part in values)) { out.append(part); return; }
        const s = document.createElement('span');
        s.className = part === '[time]' ? 'vh-delivery__time' : 'vh-delivery__dates';
        s.textContent = values[part];
        out.append(s);
      });
    };

    let timer = 0;
    const update = () => {
      clearTimeout(timer);
      const est = deliveryEstimate({
        now: new Date(),
        tz: d.tz || 'Europe/Madrid',
        cutoff: Number(d.cutoff) || 0,
        min: range[0],
        max: range[1],
        holidays
      });
      if (!est) { el.hidden = true; return; }
      const dates = span(est.from, est.to);
      if (est.left > 0) {
        const h = Math.floor(est.left / 60);
        const m = est.left % 60;
        const time = (h > 0 ? d.textHm || '[h] h [m] min' : d.textM || '[m] min').replace('[h]', h).replace('[m]', m);
        fill(d.textCountdown, { '[time]': time, '[dates]': dates });
      } else {
        fill(d.textPlain, { '[dates]': dates });
      }
      el.hidden = false;
      // Again on the next minute boundary
      timer = setTimeout(update, 60000 - (Date.now() % 60000) + 50);
    };
    update();
    // Background tabs throttle timers: catch up as soon as the page is looked at again
    bag.cleanups.push(on(document, 'visibilitychange', () => { if (!document.hidden) update(); }));
    bag.cleanups.push(() => clearTimeout(timer));
  }


  /* ----------------- init / teardown ----------------- */
  function init(root) {
    if (!root || INSTANCES.has(root)) return;
    const bag = { cleanups: [] };

    // sticky offset under header (reacts to resizes/font loads)
    const updateSticky = () => setStickyTop(root);
    const onResize = () => raf(updateSticky);
    updateSticky();

    bag.cleanups.push(on(window, 'resize', onResize, { passive: true }));
    bag.cleanups.push(on(window, 'orientationchange', onResize));

    if (document.fonts?.ready?.then) {
      document.fonts.ready.then(() => raf(updateSticky)).catch(() => {});
    }

    const hdr = document.querySelector('#shopify-section-header, .shopify-section-header') || null;
    if ('ResizeObserver' in window && hdr) {
      const ro = new ResizeObserver(() => raf(updateSticky));
      ro.observe(hdr);
      bag.cleanups.push(() => ro.disconnect());
    }

    // features (the sticky bar listens before the controls announce the first pack)
    initAccordions(root, bag);
    initStickyBar(root, bag);
    initControls(root, bag);
    initDelivery(root, bag);

    INSTANCES.set(root, bag);
  }

  function destroy(root) {
    const bag = INSTANCES.get(root);
    if (!bag) return;
    bag.cleanups.forEach(fn => { try { fn && fn(); } catch {} });
    INSTANCES.delete(root);
  }

  VH.section(ROOT_SELECTOR, init, destroy);
})();

/* ====================================================================== */
/* VH — Information Explorer (JS)                                         */
/* Logic: V2 Advanced Engine (Debounced Click + block-size animation)     */
/* ====================================================================== */
(() => {
  'use strict';

  const SELECTOR = '[data-section="vh-information"]';
  const REGISTRY = new WeakMap();

  class VHInformation {
    constructor(root) {
      this.root = root;
      this.accordion = root.querySelector('.vh-information__accordion');
      if (!this.accordion) return;

      this.allowMultiple = String(this.accordion.dataset.allowMultiple) !== 'false';
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.toggles = Array.from(this.root.querySelectorAll('.vh-information__toggle'));
      this.panels = new Map();
      this.toggleLocks = new WeakMap();

      // Bindings
      this.onToggleClick = this.onToggleClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      // Initialize
      this.toggles.forEach((btn) => {
        const id = btn.getAttribute('aria-controls');
        const panel = id && this.root.querySelector(`#${CSS.escape(id)}`);
        if (!panel) return;
        
        this.panels.set(btn, panel);
        btn.addEventListener('click', this.onToggleClick);
        btn.addEventListener('keydown', this.onKeyDown);
        
        // Ensure clean state
        panel.style.blockSize = '';
      });
    }

    destroy() {
      this.toggles.forEach((btn) => {
        btn.removeEventListener('click', this.onToggleClick);
        btn.removeEventListener('keydown', this.onKeyDown);
      });
      this.panels.forEach((panel) => this.stopAnimation(panel, true));
      this.panels.clear();
    }

    // --- Actions ---

    onToggleClick(e) {
      // 1. Prevent bubbling & default behavior
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();

      // 2. Enter/Space are handled in onKeyDown (which prevents the native click)
      this.lockedToggle(e.currentTarget);
    }

    // Debounce lock: one toggle per animation, whether from pointer or keyboard
    lockedToggle(btn) {
      if (this.toggleLocks.get(btn)) return;
      this.toggleLocks.set(btn, true);
      this.toggle(btn);
      setTimeout(() => {
        this.toggleLocks.set(btn, false);
      }, 350);
    }

    onKeyDown(e) {
      const key = e.key;
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) return;

      const btn = e.currentTarget;
      
      if (key === 'Enter' || key === ' ') {
        // Toggle directly: btn.click() would be a detail-0 click, which used to be ignored
        e.preventDefault();
        this.lockedToggle(btn);
        return;
      }

      const idx = this.toggles.indexOf(btn);
      e.preventDefault();
      
      let nextIndex = idx;
      if (key === 'ArrowDown') nextIndex = (idx + 1) % this.toggles.length;
      if (key === 'ArrowUp') nextIndex = (idx - 1 + this.toggles.length) % this.toggles.length;
      if (key === 'Home') nextIndex = 0;
      if (key === 'End') nextIndex = this.toggles.length - 1;
      
      this.toggles[nextIndex]?.focus();
    }

    toggle(btn, forceOpen = undefined) {
      const panel = this.panels.get(btn);
      if (!panel) return;

      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      const nextOpen = forceOpen !== undefined ? forceOpen : !isOpen;
      
      if (nextOpen === isOpen) return;

      if (nextOpen) {
        if (!this.allowMultiple) {
          this.toggles.forEach((other) => {
            if (other !== btn && other.getAttribute('aria-expanded') === 'true') {
              this.collapse(other, this.panels.get(other));
            }
          });
        }
        this.expand(btn, panel);
      } else {
        this.collapse(btn, panel);
      }
    }

    expand(btn, panel) {
      btn.setAttribute('aria-expanded', 'true');
      panel.hidden = false;

      if (this.reducedMotion) {
        panel.style.blockSize = '';
        return;
      }
      this.animate(panel, { from: 0, to: panel.scrollHeight, clearOnEnd: true });
    }

    collapse(btn, panel) {
      btn.setAttribute('aria-expanded', 'false');

      if (this.reducedMotion) {
        panel.hidden = true;
        panel.style.blockSize = '';
        return;
      }
      
      const current = panel.scrollHeight;
      this.animate(panel, {
        from: current,
        to: 0,
        onFinish: () => {
          panel.hidden = true;
          panel.style.blockSize = '';
        }
      });
    }

    animate(panel, { from = 0, to, clearOnEnd = false, onFinish } = {}) {
      this.stopAnimation(panel);

      const controller = new AbortController();
      panel._vhAbort = controller;

      requestAnimationFrame(() => {
        if (controller.signal.aborted) return;

        panel.style.blockSize = `${from}px`;
        // Force Reflow
        // eslint-disable-next-line no-unused-expressions
        panel.offsetHeight;

        const handleEnd = (ev) => {
          if (ev.target !== panel) return;
          // IMPORTANT: Check for block-size OR height to be safe
          if (ev.propertyName !== 'block-size' && ev.propertyName !== 'height') return;
          
          panel.removeEventListener('transitionend', handleEnd);
          if (clearOnEnd) panel.style.blockSize = '';
          if (typeof onFinish === 'function') onFinish();
        };

        panel.addEventListener('transitionend', handleEnd, { signal: controller.signal });
        panel.style.blockSize = `${to}px`;
      });
    }

    stopAnimation(panel, instant = false) {
      if (panel?._vhAbort) {
        panel._vhAbort.abort();
        panel._vhAbort = null;
      }
      if (instant) panel.style.blockSize = '';
    }
  }

  /* ------------------------- Lifecycle ------------------------- */

  const init = (root) => {
    if (!root || REGISTRY.has(root)) return;
    REGISTRY.set(root, new VHInformation(root));
  };

  const destroy = (root) => {
    const inst = REGISTRY.get(root);
    if (inst) {
      inst.destroy();
      REGISTRY.delete(root);
    }
  };

  VH.section(SELECTOR, init, destroy);
})();

/* === VH — Image Hero (vh-image-hero) === */
(function() {
  const SELECTOR = '.vh-image-hero';
  const IMG_SELECTOR = '.vh-image-hero__img';
  const ACTIVE_CLASS = 'is-loaded';
  
  // Track initialized sections to prevent double-binding
  const registry = new WeakSet();

  // 1. Shared Observer (Performance Optimization)
  // Only create one observer for the entire page
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Optional: Add 'is-inview' class if you want scroll animations later
        entry.target.classList.add('is-inview');
      }
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

  const init = (section) => {
    if (!section || registry.has(section)) return;
    
    const img = section.querySelector(IMG_SELECTOR);
    
    // A. Handle Image Loading (Fade In)
    if (img) {
      if (img.complete) {
        section.classList.add(ACTIVE_CLASS);
      } else {
        img.addEventListener('load', () => section.classList.add(ACTIVE_CLASS), { once: true });
      }
    } else {
      // Fallback for placeholder
      section.classList.add(ACTIVE_CLASS);
    }

    // B. Register for Viewport Observation
    observer.observe(section);
    registry.add(section);
  };

  const teardown = (section) => {
    if (!section) return;
    observer.unobserve(section);
    registry.delete(section);
  };

  VH.section(SELECTOR, init, teardown);
})();

/* === VH — VividText (Refactored & Safe) === */
(function() {
  const CSS_PAUSED = 'is-paused';
  const CSS_VISIBLE = 'is-visible';

  // 1. Observer Logic
  const handleIntersection = (entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const root = entry.target;
        
        // Trigger Animation
        root.classList.add(CSS_VISIBLE);
        
        // Cleanup class (optional, keeps DOM clean)
        // root.classList.remove(CSS_PAUSED); 
        
        observer.unobserve(root);
      }
    });
  };

  // 2. Init Routine
  const init = (root) => {
    if (!root || root.dataset.vhInit) return;
    root.dataset.vhInit = 'true';

    // A11y Check: Abort animation if reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // "Capture" the state: Hide it immediately so we can animate it in.
    root.classList.add(CSS_PAUSED);

    // Setup Observer
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 0.15, 
      rootMargin: '0px 0px -50px 0px'
    });

    observer.observe(root);
    root._vhObserver = observer;
  };

  // 3. Lifecycle Safety
  const teardown = (root) => {
    if (root && root._vhObserver) {
      root._vhObserver.disconnect();
      delete root._vhObserver;
      delete root.dataset.vhInit;
      root.classList.remove(CSS_PAUSED, CSS_VISIBLE);
    }
  };

  VH.section('.vh-vividtext', init, teardown);
})();

/* === VH — Deepdive (Fixed Autoplay) =================================== */
(function() {
  const NS = 'vh-deepdive';
  const observers = new WeakMap();

  const init = (root) => {
    if (!root) return;

    const wrapper = root.querySelector('[data-vh-video-wrapper]');
    const video = root.querySelector('video[data-vh-video]');

    if (!wrapper || !video) return;

    const shouldAutoplay = wrapper.dataset.autoplay === 'true';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      video.pause();
      video.removeAttribute('autoplay');
      return;
    }

    if (shouldAutoplay) {
      // FORCE MUTE: Browsers will block autoplay if this is false
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            playVideo(video);
          } else {
            pauseVideo(video);
          }
        });
      }, {
        root: null,
        threshold: 0.25
      });

      observer.observe(wrapper);
      observers.set(root, observer);
    }
  };

  const playVideo = (video) => {
    if (!video.paused) return;
    
    // Ensure mute is active right before play attempt
    video.muted = true; 
    
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => { /* autoplay blocked (e.g. low power mode) */ });
    }
  };

  const pauseVideo = (video) => {
    if (!video.paused) video.pause();
  };

  const unload = (root) => {
    if (!root) return;
    const observer = observers.get(root);
    if (observer) {
      observer.disconnect();
      observers.delete(root);
    }
  };

  VH.section(`[data-section="${NS}"]`, init, unload);
})();
/* =======================================================================
   VH — Stories & Reviews
   Phones, tablets and touch screens: the rail scrolls natively (swipe, snap
   points); this module adds the arrows and the progress line.
   Desktop (mouse or trackpad, 990px and up, "Drift with page scroll" on): the
   rail stops being a scroller and its track is moved with a transform. Where it
   sits = how far the page has scrolled through the section, plus an offset the
   shopper sets with the arrows, a sideways trackpad/wheel swipe or the keyboard.
   Any of those holds the page-linked part still for 2.5s; after that the drift
   carries on from wherever the shopper left the rail, and eases back into step
   with the page so the rail still reaches its start and its end with the section.
   (Writing rail.scrollLeft from page scroll, tried before, fought the shopper's
   own swipes and made the rail jump; a transformed track has no native scroll
   to fight, and it only moves while the page does or the shopper steers it.)
   Both: video autoplay while a tile is in view, and the mute toggles.
   ======================================================================= */
(function() {
  const SELECTOR = '[data-section="vh-stories-reviews"]';
  const DESKTOP = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const COARSE = window.matchMedia('(any-pointer: coarse)');   // touch screen laptops keep the native swipe
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const PAUSE_MS = 2500;   // page-linked drift holds still this long after the shopper steers
  const EASE = 0.14;       // share of the remaining distance covered per 60 fps frame
  const QUICK = 0.45;      // trackpads and wheels bring their own momentum: follow closely

  /* One frame of the drift, kept free of the DOM.
     s (state, updated in place): pos = where the rail is heading (0..maxRest, px),
       x = where the track is drawn (px), paused = a steering hold is on,
       anchorP/anchorQ = page progress and rail fraction (0..1) where the drift last
       picked up after the shopper steered (0/0 = rail in plain step with the page).
     f (this frame): p = page progress through the section (0..1), maxRest = travel with
       every card at rest, maxLive = travel with a card widened, now/pausedUntil (ms),
       dt (ms), follow = easing share per 60 fps frame (1 = jump).
     The rail fraction runs in a straight line from the start to the anchor and on to
     the end: no jump when the drift picks up again, and the rail still meets the page
     at both ends. Returns the target x. At the far end the target is maxLive, so the
     right edge stays put and a card widening there pushes its neighbours left. */
  const driftStep = (s, f) => {
    if (f.now < f.pausedUntil) {
      s.paused = true;                         // the shopper is steering: page scroll waits
    } else {
      if (s.paused) {                          // pick up from wherever they left it
        s.paused = false;
        s.anchorP = f.p;
        s.anchorQ = f.maxRest > 0 ? Math.min(1, Math.max(0, s.pos / f.maxRest)) : 0;
      }
      const a = s.anchorP;
      const b = s.anchorQ;
      let q;
      if (f.p <= a) q = a > 0 ? b * f.p / a : b;
      else q = a < 1 ? b + (1 - b) * (f.p - a) / (1 - a) : b;
      s.pos = q * f.maxRest;
    }
    s.pos = Math.min(f.maxRest, Math.max(0, s.pos));

    const target = f.maxRest > 0 && s.pos >= f.maxRest - 0.5 ? f.maxLive : Math.min(s.pos, f.maxLive);
    const k = f.follow >= 1 ? 1 : 1 - Math.pow(1 - f.follow, f.dt / 16.7);
    s.x += (target - s.x) * k;
    if (Math.abs(target - s.x) < 0.25) s.x = target;
    s.x = Math.min(f.maxLive, Math.max(0, s.x));
    return target;
  };

  const init = (root) => {
    if (!root || root._vhTeardown) return;

    const rail = root.querySelector('.vh-reviews__rail');
    if (!rail) return;
    const track = rail.querySelector('.vh-reviews__track');
    const items = Array.from(rail.querySelectorAll('.vh-reviews__item'));
    const arrows = root.querySelectorAll('.vh-reviews__arrow');
    const bar = root.querySelector('.vh-reviews__progress');
    const canDrift = !!track && root.dataset.drift === 'true';
    const cleanup = [];
    const on = (target, type, fn, opts) => {
      target.addEventListener(type, fn, opts);
      cleanup.push(() => target.removeEventListener(type, fn, opts));
    };

    // --- Arrows + progress line (written only when something changed) ---
    let barKey = '';
    const paint = (w, p, atStart, atEnd) => {
      const key = `${w.toFixed(4)} ${p.toFixed(4)}`;
      if (bar && key !== barKey) {
        barKey = key;
        bar.style.setProperty('--w', w.toFixed(4));
        bar.style.setProperty('--p', p.toFixed(4));
      }
      arrows.forEach((btn) => {
        const off = btn.dataset.dir === '-1' ? atStart : atEnd;
        if (btn.disabled !== off) btn.disabled = off;
      });
    };

    // --- Native: the rail is the scroller ---
    let drift = false;
    let tick = 0;
    const syncNative = () => {
      tick = 0;
      if (drift) return;
      const max = rail.scrollWidth - rail.clientWidth;
      root.classList.toggle('is-static', max <= 2);
      paint(
        Math.min(1, rail.clientWidth / Math.max(1, rail.scrollWidth)),
        max > 0 ? Math.min(1, rail.scrollLeft / max) : 0,
        rail.scrollLeft <= 2,
        rail.scrollLeft >= max - 2
      );
    };
    const onRailScroll = () => {
      if (drift) { if (rail.scrollLeft) rail.scrollLeft = 0; return; }   // nothing may scroll it now
      if (!tick) tick = requestAnimationFrame(syncNative);
    };
    // One click moves a "page" of tiles, keeping the last visible tile in view
    const nativeStep = (dir) => {
      const tile = items[0];
      if (!tile) return;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const unit = tile.offsetWidth + gap;
      const perPage = Math.max(1, Math.floor(rail.clientWidth / unit) - 1);
      rail.scrollBy({ left: dir * unit * perPage });
    };

    // --- Drift: the track moves, the page leads ---
    let near = false;      // section within reach of the viewport
    let raf = 0;
    let last = 0;          // previous frame time
    let pausedUntil = 0;
    let follow = EASE;
    let moving = false;
    const s = { pos: 0, x: 0, paused: false, anchorP: 0, anchorQ: 0 };
    const m = { W: 0, E: 0, unit: 0, total: 0, maxRest: 0, maxLive: 0 };

    const measure = () => {
      m.W = rail.clientWidth;
      m.E = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const tile = items.length ? Math.min(...items.map((el) => el.offsetWidth)) : 0;   // at rest (one card at most is widened)
      m.unit = tile + gap;
      m.total = items.length ? items.length * m.unit - gap : 0;
      m.maxRest = Math.max(0, Math.round(m.total + 2 * m.E - m.W));
      m.maxLive = Math.max(m.maxRest, Math.round(track.scrollWidth + 2 * m.E - m.W));
      root.classList.toggle('is-static', m.maxRest <= 2);
    };

    // 0 when the rail's top is 85% down the screen, 1 when its bottom is 15% from the top
    const progress = () => {
      const r = rail.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      return Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (r.height + vh * 0.7)));
    };

    const draw = () => {
      track.style.transform = `translate3d(${(-s.x).toFixed(2)}px, 0, 0)`;
      paint(
        Math.min(1, m.W / Math.max(1, m.total + 2 * m.E)),
        m.maxRest > 0 ? Math.min(1, s.x / m.maxRest) : 0,
        s.pos <= 1,
        s.pos >= m.maxRest - 1
      );
    };

    const frame = (now) => {
      raf = 0;
      if (!drift) return;
      const dt = last ? Math.min(64, now - last) : 16.7;
      last = now;
      if (now >= pausedUntil) follow = EASE;
      const target = driftStep(s, {
        p: progress(), maxRest: m.maxRest, maxLive: m.maxLive,
        now, pausedUntil, dt, follow
      });
      draw();
      // Cards only widen on hover while the rail is at rest (moving under a still pointer, they'd
      // pulse). The shift at the far end that makes room for a widened card isn't travel: counting
      // it would fold the card, unshift, widen it again, and so on.
      const anchored = target !== s.pos;
      const travelling = anchored ? s.x < m.maxRest - 0.5 : s.x !== target;
      if (moving !== travelling) {
        moving = travelling;
        root.classList.toggle('is-moving', moving);
      }
      if (s.x === target) last = 0;
      else raf = requestAnimationFrame(frame);
    };
    const kick = () => { if (drift && !raf) raf = requestAnimationFrame(frame); };

    // The shopper steers: page-linked drift holds still for a moment
    const steer = (to, ease) => {
      if (!drift) return;
      pausedUntil = performance.now() + PAUSE_MS;
      s.paused = true;
      s.pos = Math.min(m.maxRest, Math.max(0, to));
      follow = ease;
      kick();
    };
    // Lands on the card edge nearest to where a plain step would end
    const stepBy = (dir, tiles) => {
      const from = s.pos + dir * m.unit * tiles;
      let to = from;
      let best = Infinity;
      items.forEach((el) => {
        const d = Math.abs(el.offsetLeft - from);   // the track is their offset parent
        if (d < best) { best = d; to = el.offsetLeft; }
      });
      steer(to, EASE);
    };

    const setMode = () => {
      const want = canDrift && DESKTOP.matches && !COARSE.matches && !REDUCE.matches;
      if (want !== drift) {
        const from = rail.scrollLeft;
        drift = want;
        root.classList.toggle('is-drift', drift);
        if (drift) {
          measure();
          rail.scrollLeft = 0;
          const p = progress();
          // Fresh page: where the page says. Coming from a swipe (window widened): stay put.
          s.pos = from > 2 ? Math.min(m.maxRest, from) : p * m.maxRest;
          s.anchorP = from > 2 ? p : 0;
          s.anchorQ = from > 2 && m.maxRest > 0 ? s.pos / m.maxRest : 0;
          s.x = s.pos;
          s.paused = false;
          pausedUntil = 0;
          draw();
        } else {
          cancelAnimationFrame(raf);
          raf = 0;
          last = 0;
          track.style.transform = '';
          if (moving) { moving = false; root.classList.remove('is-moving'); }
          rail.style.scrollBehavior = 'auto';   // land there at once, not with the CSS smooth scroll
          rail.scrollLeft = s.x;
          rail.style.scrollBehavior = '';
        }
      }
      if (drift) { measure(); kick(); }
      else syncNative();
    };

    // --- Input ---
    arrows.forEach((btn) => {
      on(btn, 'click', () => {
        const dir = Number(btn.dataset.dir) || 1;
        if (drift) stepBy(dir, Math.max(1, Math.floor(m.W / Math.max(1, m.unit)) - 1));
        else nativeStep(dir);
      });
    });

    // A sideways trackpad swipe or shift + wheel moves the rail; a vertical one scrolls the page
    on(rail, 'wheel', (e) => {
      if (!drift || m.maxRest <= 2) return;
      let dx = e.deltaX;
      if (e.shiftKey && !dx) dx = e.deltaY;
      if (!dx || (!e.shiftKey && Math.abs(dx) < Math.abs(e.deltaY))) return;
      if (e.deltaMode === 1) dx *= 16;
      else if (e.deltaMode === 2) dx *= m.W;
      e.preventDefault();   // also keeps the browser's swipe-to-go-back from firing over the rail
      steer(s.pos + dx, QUICK);
    }, { passive: false });

    on(rail, 'keydown', (e) => {
      if (!drift || e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === 'ArrowRight') stepBy(1, 1);
      else if (e.key === 'ArrowLeft') stepBy(-1, 1);
      else if (e.key === 'Home') steer(0, EASE);
      else if (e.key === 'End') steer(m.maxRest, EASE);
      else return;
      e.preventDefault();
    });

    // Tabbing to a mute button or a review link brings its card into view
    on(rail, 'focusin', (e) => {
      if (!drift) return;
      if (rail.scrollLeft) rail.scrollLeft = 0;
      if (root.scrollLeft) root.scrollLeft = 0;
      const tile = e.target.closest('.vh-reviews__item');
      if (!tile) return;
      const left = tile.offsetLeft;
      const right = left + tile.offsetWidth;
      const view = m.W - 2 * m.E;
      if (left >= s.pos - 1 && right <= s.pos + view + 1) return;
      steer(left < s.pos ? left : right - view, EASE);
    });

    // Theme editor: selecting a review in the sidebar brings it into view
    on(root, 'shopify:block:select', (e) => {
      const tile = e.target.closest && e.target.closest('.vh-reviews__item');
      if (!tile) return;
      if (drift) steer(tile.offsetLeft, EASE);
      else tile.scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    });

    on(rail, 'scroll', onRailScroll, { passive: true });
    on(window, 'scroll', () => { if (near) kick(); }, { passive: true });
    on(window, 'resize', () => { if (drift) kick(); }, { passive: true });
    [DESKTOP, COARSE, REDUCE].forEach((mq) => on(mq, 'change', setMode));

    const ro = new ResizeObserver(() => {
      if (drift) { measure(); kick(); }
      else onRailScroll();
    });
    ro.observe(rail);
    items.forEach((el) => ro.observe(el));

    const nearIO = new IntersectionObserver((entries) => {
      near = entries.some((entry) => entry.isIntersecting);
      if (near) kick();
    }, { rootMargin: '25% 0px' });
    nearIO.observe(root);

    cleanup.push(() => {
      ro.disconnect();
      nearIO.disconnect();
      cancelAnimationFrame(raf);
      cancelAnimationFrame(tick);
      raf = 0;
      drift = false;
      if (track) track.style.transform = '';
      root.classList.remove('is-drift', 'is-moving');
    });

    setMode();

    // --- Video autoplay while at least half the tile is visible ---
    const videos = root.querySelectorAll('video[data-autoplay]');
    if (videos.length) {
      const observer = new IntersectionObserver((entries) => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        entries.forEach((entry) => {
          if (entry.isIntersecting && !reduceMotion) entry.target.play().catch(() => {});
          else entry.target.pause();
        });
      }, { threshold: 0.5 });
      videos.forEach((video) => observer.observe(video));
      cleanup.push(() => observer.disconnect());
    }

    // --- Mute toggles (aria-pressed="true" = muted) ---
    root.querySelectorAll('.vh-reviews__mute').forEach((btn) => {
      const video = btn.closest('.vh-reviews__media')?.querySelector('video');
      if (!video) return;
      const toggleMute = (e) => {
        e.preventDefault();
        e.stopPropagation();
        video.muted = !video.muted;
        if (!video.muted) video.play().catch(() => {});
        btn.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
      };
      btn.addEventListener('click', toggleMute);
      cleanup.push(() => btn.removeEventListener('click', toggleMute));
    });

    root._vhTeardown = () => cleanup.forEach((fn) => fn());
  };

  const unload = (root) => {
    if (root && root._vhTeardown) {
      root._vhTeardown();
      delete root._vhTeardown;
    }
  };

  VH.section(SELECTOR, init, unload);
})();

 
/* ===== VH — Word Splash (section) ===== */
(function () {
  function init(root) {
    if (!root || !root.matches('[data-section="vh-word-splash"]')) return;
    const stack = root.querySelector('.vh-word-splash__stack');
    if (!stack) return;

    // Trigger the “rest” state once visible.
    if (stack.dataset.animate === 'true') {
      const stagger = Number(stack.dataset.stagger || 0);

      stack.querySelectorAll('.vh-word-splash__line').forEach((el, i) => {
        const base = parseInt(el.style.getPropertyValue('--delay') || '0', 10);
        el.style.setProperty('--delay', `${base + i * stagger}ms`);
      });

      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            stack.classList.add('rest-now');
            io.disconnect();
            break;
          }
        }
      }, { threshold: 0.25 });

      io.observe(root);
      root.__vhWS = { io };
    }
  }

  function unload(root) {
    if (root?.__vhWS?.io) root.__vhWS.io.disconnect();
    if (root) delete root.__vhWS;
  }

  VH.section('[data-section="vh-word-splash"]', init, unload);
})();
/* === VH — Global Architecture === */

(function() {
  'use strict';

  // --- 1. DOUBLE VIDEO LOGIC ---
  const DoubleVideo = {
    SEL: {
      ROOT: '[data-section="vh-double-video"]',
      VIDEO: '.vh-double-video__video',
      READY_CLASS: 'is-ready'
    },
    registry: new WeakMap(),

    attemptPlay: (video) => {
      if (video.paused) {
        const p = video.play();
        if (p && p.catch) p.catch(() => { /* Autoplay blocked */ });
      }
    },

    attemptPause: (video) => {
      if (!video.paused) video.pause();
    },

    init: (root) => {
      if (!root || DoubleVideo.registry.has(root)) return;
      const videos = Array.from(root.querySelectorAll(DoubleVideo.SEL.VIDEO));
      
      // Each video fades in over its poster once real frames are playing
      videos.forEach((v) => {
        const ready = () => v.classList.add(DoubleVideo.SEL.READY_CLASS);
        if (!v.paused && v.readyState >= 3) ready();
        else v.addEventListener('playing', ready, { once: true });
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) videos.forEach(DoubleVideo.attemptPlay);
          else videos.forEach(DoubleVideo.attemptPause);
        });
      }, { rootMargin: '50% 0px 50% 0px', threshold: 0.01 });

      observer.observe(root);
      DoubleVideo.registry.set(root, { observer });
    },

    unload: (root) => {
      const record = DoubleVideo.registry.get(root);
      if (!record) return;
      if (record.observer) record.observer.disconnect();
      DoubleVideo.registry.delete(root);
    }
  };

  // --- 2. GLOBAL SMOOTH SCROLL ---
  const SmoothScroll = {
    init: () => {
      let prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', e => {
        prefersReduced = e.matches;
      });

      document.addEventListener('click', (event) => {
        const link = event.target.closest && event.target.closest('a[data-scroll]');
        if (!link) return;

        const href = (link.getAttribute('href') || '').trim();
        if (!href || href.charAt(0) !== '#') return;

        const target = document.getElementById(href.slice(1));
        if (!target) return;

        event.preventDefault();

        const container = link.closest('[data-scroll-offset]');
        const offset = container ? (parseInt(container.getAttribute('data-scroll-offset'), 10) || 0) : 0;

        window.scrollTo({
          top: window.pageYOffset + target.getBoundingClientRect().top - offset,
          behavior: prefersReduced ? 'auto' : 'smooth'
        });

        target.setAttribute('tabindex', '-1');
        try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
        setTimeout(() => target.removeAttribute('tabindex'), 250);
      }, { capture: true, passive: false });
    }
  };

  // --- 3. LIFECYCLE HOOKS ---
  VH.section(DoubleVideo.SEL.ROOT, DoubleVideo.init, DoubleVideo.unload);
  SmoothScroll.init();
})();
/* === VH — More Splash & Contact Initialization === */

(function() {
  // --- More Splash Logic ---
  const initMoreSplash = (root) => {
    // Determine if the target is the section itself or contains it
    const section = root.classList?.contains('vh-more-splash') ? root : root.querySelector('.vh-more-splash');
    if (!section || section.dataset.vhInitialized) return;

    if (!('IntersectionObserver' in window)) {
      section.classList.add('vh-more-splash--visible');
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          section.classList.add('vh-more-splash--visible');
          observer.unobserve(section);
        }
      });
    }, { threshold: 0.3 });

    observer.observe(section);
    section._vhSplashObserver = observer;
    section.dataset.vhInitialized = "true";
  };

  const teardownMoreSplash = (root) => {
    const section = root.classList?.contains('vh-more-splash') ? root : root.querySelector('.vh-more-splash');
    if (!section) return;
    
    if (section._vhSplashObserver) {
      section._vhSplashObserver.disconnect();
      delete section._vhSplashObserver;
    }
    delete section.dataset.vhInitialized;
  };

  // --- Contact Section Logic ---
  const initContact = (root) => {
    const section = root.classList?.contains('vh-contact') ? root : root.querySelector('.vh-contact');
    if (!section || section.dataset.vhInitialized) return;

    section.querySelectorAll('.vh-contact__error-link').forEach(a => {
      a.addEventListener('click', (e) => {
        const href = a.getAttribute('href');
        if (!href || href.charAt(0) !== '#') return;
        const el = section.querySelector(href);
        if (el) el.focus();
      }, { passive: true });
    });
    section.dataset.vhInitialized = "true";
  };

  const teardownContact = (root) => {
    const section = root.classList?.contains('vh-contact') ? root : root.querySelector('.vh-contact');
    if (!section) return;
    delete section.dataset.vhInitialized;
  };

  VH.section('.vh-more-splash', initMoreSplash, teardownMoreSplash);
  VH.section('.vh-contact', initContact, teardownContact);
})();
/* ===========================================================
   VH — Footer Reveal (JS Logic)
   Standard: Vivid Engineering v2
   The reveal is fixed behind the page; a bottom margin on the footer opens the window it
   shows through. With reduced motion the CSS makes it a plain block, so no margin then.
   Its video gets the phone or desktop sources (<template data-vh-reveal-src>) to suit the
   screen, so only one file ever downloads; preload="none" holds it until the footer is near.
   =========================================================== */

(function() {
  const SELECTORS = {
    reveal: '.vh-reveal',
    footer: '.section-vh-footer', // Core Dawn/Vivid footer class
    video: 'video[data-vh-reveal-video]'
  };
  const phone = window.matchMedia('(max-width: 749px)');   // same breakpoint as vh-footer-reveal.css
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Cleanup per section, for the theme editor
  const cleanups = new WeakMap();

  // "Size the reveal to the video": the media's height at the screen's width, at most one
  // screen. Its shape is the video's own once known, else the one the Liquid read off the image.
  const fitHeight = (revealSection) => {
    const video = revealSection.querySelector(SELECTORS.video);
    let aspect = video && video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : 0;
    if (!aspect) aspect = parseFloat(phone.matches ? revealSection.dataset.aspectPhone : revealSection.dataset.aspect) || 16 / 9;
    return Math.round(Math.min(window.innerWidth / aspect, window.innerHeight));
  };

  const setFooterMargin = (revealSection, footer) => {
    if (!revealSection || !footer) return;

    // Static reveal (reduced motion): no window to open
    if (reduceMotion.matches) {
      footer.style.marginBottom = '';
      return;
    }

    // 1. Get computed style to read the CSS variables
    const style = getComputedStyle(revealSection);

    // 2. Read the specific height tokens set in the Liquid schema
    // We do NOT use offsetHeight here because the CSS includes a +400px buffer
    const heightVar = phone.matches
      ? style.getPropertyValue('--vh-reveal-height-mobile')
      : style.getPropertyValue('--vh-reveal-height-desktop');

    // 3. Apply as margin to the main footer to create the "window"
    if (revealSection.classList.contains('vh-reveal--fit')) {
      const px = `${fitHeight(revealSection)}px`;
      revealSection.style.setProperty('--vh-reveal-fit', px);
      footer.style.marginBottom = px;
    } else if (heightVar) {
      footer.style.marginBottom = heightVar.trim();
    }
  };

  // Puts the sources that suit the screen into the video (phones fall back to the desktop
  // set). On a later swap the video reloads, and plays on if it was playing.
  const pickSources = (revealSection) => {
    const video = revealSection.querySelector(SELECTORS.video);
    if (!video) return;
    const tpl = (phone.matches && revealSection.querySelector('template[data-vh-reveal-src="phone"]'))
      || revealSection.querySelector('template[data-vh-reveal-src="desktop"]');
    const want = tpl ? tpl.dataset.vhRevealSrc : 'none';
    if (video.dataset.vhSrc === want) return;

    const isSwap = video.dataset.vhSrc !== undefined;
    const wasPlaying = !video.paused;
    video.dataset.vhSrc = want;
    video.querySelectorAll('source').forEach((s) => s.remove());
    video.classList.remove('is-playing');   // the still shows until the new frames play
    if (tpl) video.append(tpl.content.cloneNode(true));
    if (isSwap) {
      video.load();   // with preload="none" this fetches nothing yet
      if (tpl && wasPlaying) video.play().catch(() => {});
    }
  };

  const init = (container) => {
    // Scoping: Ensure we are only initializing if this specific section is present
    const revealSection = container.querySelector(SELECTORS.reveal);
    if (!revealSection) return;

    // Before anything can call play() (the in-view observer reports asynchronously)
    pickSources(revealSection);

    // Find the main footer (usually outside this section)
    const footer = document.querySelector(SELECTORS.footer);
    if (!footer) {
      console.warn('VH — Footer Reveal: Main footer element not found. Effect disabled.');
    }
    const update = () => setFooterMargin(revealSection, footer);

    // A. Initial Calculation
    requestAnimationFrame(update);

    // B. Resize Observer (More performant than window.resize)
    const resizeObserver = new ResizeObserver(() => window.requestAnimationFrame(update));
    if (footer) resizeObserver.observe(document.body);

    // C. Crossing the phone breakpoint swaps the video; the motion preference toggles the margin
    const onPhoneChange = () => { pickSources(revealSection); update(); };
    phone.addEventListener('change', onPhoneChange);
    reduceMotion.addEventListener('change', update);

    // D. Sized to the video: its real shape is known once its metadata is in; the screen's
    // height (the cap) can change without the body resizing
    const video = revealSection.querySelector(SELECTORS.video);
    if (video) video.addEventListener('loadedmetadata', update);
    window.addEventListener('resize', update, { passive: true });

    cleanups.set(container, () => {
      resizeObserver.disconnect();
      phone.removeEventListener('change', onPhoneChange);
      reduceMotion.removeEventListener('change', update);
      if (video) video.removeEventListener('loadedmetadata', update);
      window.removeEventListener('resize', update);
    });
  };

  const teardown = (container) => {
    // 1. Disconnect observers and listeners
    const cleanup = cleanups.get(container);
    if (cleanup) {
      cleanup();
      cleanups.delete(container);
    }

    // 2. Cleanup Styles (Reset footer margin)
    const footer = document.querySelector(SELECTORS.footer);
    if (footer) {
      footer.style.marginBottom = '';
    }
  };

  const wrapper = (el) => el.closest('.shopify-section') || el.parentNode;
  VH.section('[data-section-type="vh-footer-reveal"]', (el) => init(wrapper(el)), (el) => teardown(wrapper(el)));

})();

/* === VH — Reward Popup ======================================
   Opens once after a delay, then stays away for "expiry days" (localStorage).
   Entrance: un-hide, force a reflow so the start state is painted, then .is-active fades
   the card in as it rises. Exit: drop .is-active and hide the parts only once the card has
   faded out (a timer covers the case where transitionend never fires). */
(function () {
  const SECTION_SELECTOR = '[data-section="vh-reward-popup"]';
  const SEEN_KEY_PREFIX = 'vhRewardPopupSeen_';
  const PENDING_KEY_PREFIX = 'vhRewardPopupPending_';   // sent off to Shopify's captcha page
  const PENDING_MAX_AGE_MS = 30 * 60 * 1000;
  const SUBMIT_TIMEOUT_MS = 10000;
  const CLOSE_FALLBACK_MS = 500;
  const stateMap = new WeakMap();

  function getFocusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((el) => !el.hasAttribute('aria-hidden') && el.getClientRects().length > 0);
  }

  function parts(section) {
    return {
      overlay: section.querySelector('[data-vh-popup-overlay]'),
      dialog: section.querySelector('[data-vh-popup-dialog]'),
      card: section.querySelector('[data-vh-popup-card]')
    };
  }

  function shouldShowOnDevice(section) {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const showDesktop = section.dataset.popupShowDesktop === 'true';
    const showMobile = section.dataset.popupShowMobile === 'true';
    return (isMobile && showMobile) || (!isMobile && showDesktop);
  }

  function hasSeenRecently(storageKey, expiryDays) {
    if (!window.localStorage || !expiryDays) return false;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return false;
      const data = JSON.parse(raw);
      if (!data.time) return false;
      const age = Date.now() - data.time;
      const maxAge = expiryDays * 24 * 60 * 60 * 1000;
      return age < maxAge;
    } catch (e) { return false; }
  }

  function markSeen(storageKey) {
    if (!window.localStorage) return;
    try { window.localStorage.setItem(storageKey, JSON.stringify({ time: Date.now() })); } catch (e) {}
  }

  function openPopup(section) {
    const { overlay, dialog } = parts(section);
    if (!overlay || !dialog) return;
    const st = stateMap.get(section) || {};
    if (st.isOpen) return;
    if (st.cancelHide) st.cancelHide();   // reopened mid-exit: stay on screen

    // --- SCROLL LOCK ---
    document.body.style.overflow = 'hidden';
    // -------------------

    overlay.hidden = false;
    dialog.hidden = false;
    void dialog.offsetWidth;   // paint the start state first, so the entrance transitions
    section.classList.add('is-active');

    stateMap.set(section, {
      ...st,
      previousActive: document.activeElement, isOpen: true, cancelHide: null
    });

    // The dialog itself takes focus: screen readers announce it, and phones don't pop the keyboard
    dialog.focus({ preventScroll: true });
  }

  function closePopup(section) {
    const { overlay, dialog, card } = parts(section);
    const st = stateMap.get(section) || {};
    if (!st.isOpen || !overlay || !dialog) return;

    // --- SCROLL UNLOCK ---
    document.body.style.overflow = '';
    // ---------------------

    section.classList.remove('is-active');

    // Hide once the card has faded out
    let timer = 0;
    const cancel = () => {
      window.clearTimeout(timer);
      if (card) card.removeEventListener('transitionend', done);
    };
    const done = (e) => {
      if (e && (e.target !== card || e.propertyName !== 'opacity')) return;
      cancel();
      overlay.hidden = true;
      dialog.hidden = true;
    };
    if (card) card.addEventListener('transitionend', done);
    timer = window.setTimeout(done, CLOSE_FALLBACK_MS);

    if (st.previousActive && typeof st.previousActive.focus === 'function') {
      st.previousActive.focus({ preventScroll: true });
    }

    const storageKey = SEEN_KEY_PREFIX + (section.dataset.sectionId || '');
    const expiryDays = parseInt(section.dataset.popupExpiryDays || '0', 10);
    if (expiryDays > 0) markSeen(storageKey);

    stateMap.set(section, { ...st, isOpen: false, cancelHide: cancel });
  }

  function handleKeydown(event, section) {
    const st = stateMap.get(section);
    if (!st || !st.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closePopup(section);
      return;
    }

    if (event.key === 'Tab') {
      // Read fresh each time: the form gives way to the code after a reveal
      const focusables = getFocusable(section.querySelector('[data-vh-popup-dialog]'));
      if (!focusables.length) { event.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const inside = focusables.includes(document.activeElement);   // false on the dialog itself

      if (event.shiftKey) {
        if (!inside || document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (!inside || document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  }

  // The code takes the form's place; focus moves to it so it is read out
  function revealCode(section) {
    const form = section.querySelector('[data-vh-popup-form]');
    const successEl = section.querySelector('[data-vh-popup-success]');
    const codeEl = section.querySelector('[data-vh-popup-code]');
    if (form) { form.hidden = true; form.removeAttribute('aria-busy'); }
    if (codeEl) codeEl.hidden = false;
    if (successEl) {
      successEl.hidden = false;
      if ((stateMap.get(section) || {}).isOpen) successEl.focus({ preventScroll: true });
    }
  }

  // Shopify may answer a sign-up with a captcha page; any other answer means it got the email
  const isChallenge = (url) => /\/challenge(?:[/?#]|$)/.test(String(url || ''));

  // The form is a Shopify customer form. It is posted in the background, and the code shows
  // once Shopify has answered. If the network fails the code shows anyway (the shopper did
  // their part). If Shopify wants a captcha first, the form is submitted normally so the
  // shopper can solve it; a note in localStorage lets the code show when they come back.
  function initForm(section) {
    const form = section.querySelector('[data-vh-popup-form]');
    if (!form) return () => {};
    const emailInput = form.querySelector('input[type="email"]');
    const errorEl = form.querySelector('[data-vh-popup-error]');
    const sectionId = section.dataset.sectionId || '';
    const storageKey = SEEN_KEY_PREFIX + sectionId;
    const invalidMsg = (errorEl && errorEl.dataset.msgInvalid) || '';
    let sending = false;

    const onSubmit = async (e) => {
      e.preventDefault();
      if (sending) return;
      const value = (emailInput && emailInput.value || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
        if (errorEl) errorEl.textContent = invalidMsg;
        if (emailInput) {
          emailInput.setAttribute('aria-invalid', 'true');
          emailInput.focus();
        }
        return;
      }
      if (errorEl) errorEl.textContent = '';
      if (emailInput) {
        emailInput.removeAttribute('aria-invalid');
        emailInput.value = value;
      }

      sending = true;
      form.setAttribute('aria-busy', 'true');
      let challenged = false;
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ctrl ? window.setTimeout(() => ctrl.abort(), SUBMIT_TIMEOUT_MS) : 0;
      try {
        const res = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: ctrl ? ctrl.signal : undefined
        });
        // Redirects are followed: res.url is where Shopify sent the post
        // (…?customer_posted=true on success, /challenge when it wants a captcha)
        challenged = isChallenge(res.url);
      } catch (err) {
        // Offline, blocked or timed out: reveal the code anyway, quietly
      }
      window.clearTimeout(timer);

      markSeen(storageKey);
      if (challenged) {
        try { window.localStorage.setItem(PENDING_KEY_PREFIX + sectionId, String(Date.now())); } catch (err) {}
        // The browser's own submit (no submit event), so the captcha page opens
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
      sending = false;
      revealCode(section);
    };

    form.addEventListener('submit', onSubmit);
    return () => form.removeEventListener('submit', onSubmit);
  }

  // Back from the captcha page: Shopify returns to this page with ?customer_posted=true
  function takePendingReveal(section) {
    const key = PENDING_KEY_PREFIX + (section.dataset.sectionId || '');
    let since = 0;
    try { since = parseInt(window.localStorage.getItem(key) || '0', 10); } catch (err) { return false; }
    if (!since) return false;
    const posted = /[?&]customer_posted=true(?:&|$)/.test(window.location.search);
    const fresh = Date.now() - since < PENDING_MAX_AGE_MS;
    if (posted || !fresh) {
      try { window.localStorage.removeItem(key); } catch (err) {}
    }
    return posted && fresh;
  }

  function initRewardPopup(section) {
    if (!section || stateMap.has(section)) return;
    const enabled = section.dataset.popupEnabled === 'true';
    if (!enabled) return;

    // Back from the captcha with the sign-up done: show the code straight away
    const pendingReveal = takePendingReveal(section);
    if (!pendingReveal && !shouldShowOnDevice(section)) return;

    const delay = parseInt(section.dataset.popupDelay || '0', 10);
    const expiryDays = parseInt(section.dataset.popupExpiryDays || '0', 10);
    const storageKey = SEEN_KEY_PREFIX + (section.dataset.sectionId || '');

    // Seen (closed or signed up) within "Days before showing again": stay away
    if (!pendingReveal && expiryDays > 0 && hasSeenRecently(storageKey, expiryDays)) return;

    const overlay = section.querySelector('[data-vh-popup-overlay]');
    const dialog = section.querySelector('[data-vh-popup-dialog]');
    const closeBtn = section.querySelector('[data-vh-popup-close]');

    if (!overlay || !dialog) return;

    // A click anywhere outside the card closes it (the dialog's own padding counts as outside)
    // Close on a press that starts AND ends outside the card (a text drag that ends in the
    // dialog's padding fires its click on the dialog, and shouldn't close it)
    let pressedOutside = false;
    const onPress = (e) => { pressedOutside = e.target === overlay || e.target === dialog; };
    const onOverlayClick = (e) => {
      if (pressedOutside && (e.target === overlay || e.target === dialog)) closePopup(section);
      pressedOutside = false;
    };
    const onCloseClick = () => closePopup(section);
    const onKeydown = (e) => handleKeydown(e, section);

    overlay.addEventListener('pointerdown', onPress, { passive: true });
    dialog.addEventListener('pointerdown', onPress, { passive: true });
    overlay.addEventListener('click', onOverlayClick, { passive: true });
    dialog.addEventListener('click', onOverlayClick, { passive: true });
    closeBtn && closeBtn.addEventListener('click', onCloseClick);
    document.addEventListener('keydown', onKeydown);

    const formCleanup = initForm(section);

    // Never open over the cart drawer: wait until the shopper has closed it
    const tryOpen = () => {
      if (document.documentElement.classList.contains('vh-drawer-open')) {
        const st = stateMap.get(section);
        if (st) st.timeoutId = window.setTimeout(tryOpen, 1500);
        return;
      }
      openPopup(section);
      if (pendingReveal) revealCode(section);
    };
    const timeoutId = window.setTimeout(tryOpen, pendingReveal ? 400 : Math.max(0, delay));

    stateMap.set(section, {
      timeoutId, isOpen: false, overlayHandler: onOverlayClick, pressHandler: onPress,
      closeHandler: onCloseClick, keydownHandler: onKeydown, formCleanup
    });
  }

  function unloadRewardPopup(section) {
    const st = stateMap.get(section);
    if (!section || !st) return;
    const { overlay, dialog } = parts(section);
    const closeBtn = section.querySelector('[data-vh-popup-close]');

    if (overlay && st.overlayHandler) overlay.removeEventListener('click', st.overlayHandler);
    if (dialog && st.overlayHandler) dialog.removeEventListener('click', st.overlayHandler);
    if (overlay && st.pressHandler) overlay.removeEventListener('pointerdown', st.pressHandler);
    if (dialog && st.pressHandler) dialog.removeEventListener('pointerdown', st.pressHandler);
    if (closeBtn && st.closeHandler) closeBtn.removeEventListener('click', st.closeHandler);
    if (st.keydownHandler) document.removeEventListener('keydown', st.keydownHandler);
    if (st.formCleanup) st.formCleanup();
    if (st.timeoutId) window.clearTimeout(st.timeoutId);
    if (st.cancelHide) st.cancelHide();

    if(st.isOpen) document.body.style.overflow = '';

    stateMap.delete(section);
  }

  VH.section(SECTION_SELECTOR, initRewardPopup, unloadRewardPopup);
})();

/* === VH — Judge.me (Lite) Lifecycle === */
(function() {
  const init = (root) => {
    // 1. Guard Clause: specific to this section
    if (!root || !root.classList.contains('vh-judgeme-lite')) return;

    // 2. Check if Judge.me global object exists
    if (window.jdgm && window.jdgm.widget) {
      // 3. Force re-install of the widget on the new DOM elements
      // We use a small timeout to ensure the DOM injection is complete
      setTimeout(() => {
        window.jdgm.widget.autoInstall();
      }, 100);
    }
  };

  VH.section('.vh-judgeme-lite', init);
})();

/* === VH — Collapsible (Accordion Logic v3) === */
(function() {
  const NS = 'vh-collapsible';
  const TRANSITION_MS = 400; // Must match CSS 0.4s

  // Helper: Smoothly close a details element
  const closeDetails = (details) => {
    if (!details.hasAttribute('open')) return;
    
    // 1. Mark as closing (locks interaction)
    details.dataset.status = 'closing';
    
    // 2. Wait for CSS transition to finish, then actually close
    setTimeout(() => {
      details.removeAttribute('open');
      details.dataset.status = 'idle';
    }, TRANSITION_MS); 
  };

  // Helper: Open a details element
  const openDetails = (details, group) => {
    // 1. Close siblings (Accordion behavior)
    if (group) {
      group.forEach(sibling => {
        if (sibling !== details && sibling.hasAttribute('open')) {
          closeDetails(sibling);
        }
      });
    }
    
    // 2. Open self (CSS handles the slide-down via grid-template-rows)
    details.setAttribute('open', '');
    details.dataset.status = 'opening';
    
    // 3. Reset status after animation
    setTimeout(() => {
      details.dataset.status = 'idle';
    }, TRANSITION_MS);
  };

  const init = (root) => {
    // Safety check + prevent double init
    if (!root || root.dataset.vhInited) return;
    root.dataset.vhInited = 'true';

    // Find all items in this specific section
    const items = root.querySelectorAll('details.vh-collapsible__item');
    
    items.forEach(details => {
      const summary = details.querySelector('summary');
      if (!summary) return;
      
      const onClick = (e) => {
        e.preventDefault(); // STOP native instant toggle
        e.stopPropagation();

        // Ignore clicks if currently animating
        if (details.dataset.status === 'closing' || details.dataset.status === 'opening') return;

        if (details.hasAttribute('open')) {
          closeDetails(details);
        } else {
          openDetails(details, items);
        }
      };

      summary.addEventListener('click', onClick);
      
      // Store reference for clean teardown
      details._vhClick = onClick;
    });
  };

  const unload = (root) => {
    if (!root) return;
    const items = root.querySelectorAll('details.vh-collapsible__item');
    items.forEach(details => {
      if (details._vhClick) {
        details.querySelector('summary').removeEventListener('click', details._vhClick);
        delete details._vhClick;
      }
    });
    delete root.dataset.vhInited;
  };

  VH.section(`[data-section="${NS}"]`, init, unload);
})();
/* =========================================================
   DARK EDITION — reveal on scroll, video fades, counters
   ========================================================= */
(function () {
  const root = document.documentElement;
  root.classList.add('vh-ready');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Reveal: [data-vh-reveal] settles into place when it enters the screen */
  const revealIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      e.target.dispatchEvent(new CustomEvent('vh:in'));
      revealIO.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.06 });

  const watch = (el) => {
    if (reduce.matches) { el.classList.add('is-in'); el.dispatchEvent(new CustomEvent('vh:in')); return; }
    revealIO.observe(el);
  };
  VH.section('[data-vh-reveal]', watch, (el) => revealIO.unobserve(el));

  /* Older sections get the same reveal on their titles and cards, but only for things
     still below the fold when the page loads (tagging what's on screen would flicker). */
  const AUTO = [
    '.vh-products__title', '.vh-products__grid', '.vh-reviews__title', '.vh-reviews__rail',
    '.vh-comparison__title', '.vh-comparison__table', '.vh-collapsible__title', '.vh-collapsible__item',
    '.vh-steps__title', '.vh-steps__item', '.vh-formula__hero', '.vh-formula__media', '.vh-formula__capsules',
    '.vh-duo-panels__panel', '.vh-duo-panels__text', '.vh-judgeme-lite__heading', '.vh-information__headline', '.vh-sources__heading',
    '.vh-ct-title', '.vh-image-hero__title', '.vh-faqpage__group', '.vh-circle-links__title'
  ].join(',');
  const autoTag = (scope) => {
    const fold = window.innerHeight;
    scope.querySelectorAll(AUTO).forEach((el) => {
      if (el.hasAttribute('data-vh-reveal') || el.getBoundingClientRect().top < fold) return;
      const sibs = el.parentElement ? [...el.parentElement.children].filter((c) => c.matches(AUTO)) : [];
      el.style.setProperty('--i', Math.min(Math.max(sibs.indexOf(el), 0), 6));
      el.setAttribute('data-vh-reveal', '');
      watch(el);
    });
  };
  autoTag(document);
  document.addEventListener('shopify:section:load', (e) => autoTag(e.target));

  /* Background videos: shown only once frames are actually playing */
  VH.section('video[data-vh-fade]', (v) => {
    const on = () => v.classList.add('is-playing');
    if (!v.paused && v.readyState > 2) on();
    v.addEventListener('playing', on);
    v.addEventListener('loadeddata', () => { if (reduce.matches || !v.autoplay) on(); });
  });

  /* Counters: <span data-vh-count="975">975</span> counts up the first time it shows */
  const fmt = new Intl.NumberFormat(root.lang || undefined);
  VH.section('[data-vh-count]', (el) => {
    const target = parseFloat(el.dataset.vhCount);
    if (!isFinite(target) || reduce.matches) return;
    const run = () => {
      const t0 = performance.now(), dur = 1400;
      const step = (t) => {
        const k = Math.min(1, (t - t0) / dur), eased = 1 - Math.pow(1 - k, 3);
        el.textContent = fmt.format(Math.round(target * eased));
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const host = el.closest('[data-vh-reveal]') || el;
    if (host.classList.contains('is-in')) run();
    else host.addEventListener('vh:in', run, { once: true });
    if (host === el) watch(el);
  });
})();

/* =========================================================
   VH — Shop menu (sections/vh-header.liquid, "Shop item" blocks)
   Computers: hovering or focusing "shop" opens the glass panel under the bar; it closes a
   moment after the pointer leaves both, when focus moves elsewhere, or on Escape.
   Phones: "shop" in the menu unfolds the cards. Card videos load and play only while
   their cards show, and never with reduced motion.
   ========================================================= */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const videos = (box, on) => box.querySelectorAll('video[data-vh-shop-video]').forEach((v) => {
    if (on && !reduce.matches) {
      if (v.preload !== 'auto') v.preload = 'auto';
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } else if (!v.paused) v.pause();
  });
  const playing = (v) => v.classList.add('is-playing');

  const init = (header) => {
    if (header._vhShop) return;
    const link = header.querySelector('[data-vh-shop-link]');
    const panel = header.querySelector('[data-vh-shopmenu]');
    const toggle = header.querySelector('[data-vh-shop-toggle]');
    const mobile = header.querySelector('[data-vh-shop-mobile]');
    const offs = [];
    const on = (el, type, fn) => { if (!el) return; el.addEventListener(type, fn); offs.push(() => el.removeEventListener(type, fn)); };
    header.querySelectorAll('video[data-vh-shop-video]').forEach((v) => on(v, 'playing', () => playing(v)));

    if (link && panel) {
      let timer = 0;
      const set = (open) => {
        clearTimeout(timer);
        if (panel.classList.contains('is-open') === open) return;
        panel.classList.toggle('is-open', open);
        link.setAttribute('aria-expanded', String(open));
        videos(panel, open);
      };
      const later = () => { clearTimeout(timer); timer = setTimeout(() => set(false), 220); };
      [link, panel].forEach((el) => {
        on(el, 'pointerenter', (e) => { if (e.pointerType !== 'touch') set(true); });
        on(el, 'pointerleave', (e) => { if (e.pointerType !== 'touch') later(); });
        on(el, 'focusin', () => set(true));
        on(el, 'focusout', (e) => { if (!link.contains(e.relatedTarget) && !panel.contains(e.relatedTarget)) later(); });
      });
      on(header, 'keydown', (e) => {
        if (e.key !== 'Escape' || !panel.classList.contains('is-open')) return;
        set(false);
        link.focus();
      });
      offs.push(() => clearTimeout(timer));
    }

    if (toggle && mobile) {
      on(toggle, 'click', () => {
        const open = !mobile.classList.contains('is-open');
        mobile.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        videos(mobile, open);
      });
      // Closing the phone menu folds the cards back and stops their videos
      const menu = mobile.closest('.vh-menu');
      if (menu && window.MutationObserver) {
        const mo = new MutationObserver(() => {
          if (menu.classList.contains('is-open') || !mobile.classList.contains('is-open')) return;
          mobile.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          videos(mobile, false);
        });
        mo.observe(menu, { attributes: true, attributeFilter: ['class'] });
        offs.push(() => mo.disconnect());
      }
    }

    header._vhShop = () => { offs.forEach((fn) => fn()); delete header._vhShop; };
  };
  const teardown = (header) => { if (header._vhShop) header._vhShop(); };
  if (window.VH && typeof window.VH.section === 'function') VH.section('.vh-header', init, teardown);
  else document.querySelectorAll('.vh-header').forEach(init);
})();

/* =========================================================
   SECTION BY SECTION ON PHONES (html.vh-snap)
   vh-base.css §14 makes every section start a stop. Sections taller than the
   screen get invisible stops every ~85% of a screen (so they scroll through in
   steps instead of being skipped), plus one where their last screenful begins.
   One stop at the very end keeps the footer and its reveal reachable.
   Stops are measured off the small viewport (100svh) and only rebuilt when the
   width or the content changes, so the iOS toolbar showing/hiding never moves them.
   ========================================================= */
(() => {
  const root = document.documentElement;
  if (!root.classList.contains('vh-snap')) return;
  const phone = window.matchMedia('(max-width: 749px)');
  const body = document.body;
  let marks = [];
  let timer = 0;
  let lastW = window.innerWidth;

  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = 'display:block;position:fixed;top:0;left:0;width:0;height:100svh;pointer-events:none;visibility:hidden';
  body.appendChild(probe);

  const end = document.createElement('div');
  end.className = 'vh-snap-end';
  end.setAttribute('aria-hidden', 'true');

  const docY = (el) => el.getBoundingClientRect().top - body.getBoundingClientRect().top;

  const build = () => {
    marks.forEach((m) => m.remove());
    marks = [];
    end.remove();   // measured fresh below, so a shorter page can shrink back
    if (!phone.matches) return;
    const screen = probe.offsetHeight || window.innerHeight;
    if (screen < 240) return;   // no real viewport yet (hidden tab, collapsed frame): nothing to measure
    const step = Math.round(screen * 0.85);
    const sections = document.querySelectorAll('#MainContent > .shopify-section:not(.vh-blend-section), .shopify-section-group-footer-group');
    const frag = document.createDocumentFragment();
    const add = (y) => {
      const m = document.createElement('div');
      m.className = 'vh-snap-mark';
      m.setAttribute('aria-hidden', 'true');
      m.style.top = `${Math.round(y)}px`;
      frag.appendChild(m);
      marks.push(m);
    };
    add(0);   // the very top (an in-flow announcement bar sits above the first section)
    sections.forEach((sec) => {
      const h = sec.offsetHeight;
      if (h <= screen * 1.15) return;
      const top = docY(sec);
      // A scroll-scrubbed scene stops where it says (data-vh-stops: fractions of its scroll),
      // or in the middle of each of its data-vh-stages. Lost days only counts once pinned.
      const scene = sec.querySelector('[data-vh-night]:not(.is-static), [data-vh-lost].is-live');
      if (scene) {
        const span = h - screen;
        const at = (scene.dataset.vhStops || '').split(',').map(parseFloat).filter((f) => f >= 0 && f <= 1);
        if (at.length) at.forEach((f) => add(top + span * f));
        else {
          const n = parseInt(scene.dataset.vhStages, 10) || scene.querySelectorAll('.vh-night__stop').length || 1;
          for (let i = 0; i < n; i++) add(top + span * (i + 0.5) / n);
        }
        add(top + span);
        return;
      }
      for (let y = step; y < h - screen - step * 0.3; y += step) add(top + y);
      add(top + h - screen);
    });
    end.style.top = `${Math.max(0, root.scrollHeight - docY(root) - 1)}px`;
    frag.appendChild(end);
    body.appendChild(frag);
  };
  const soon = () => { clearTimeout(timer); timer = setTimeout(build, 180); };

  window.addEventListener('resize', () => {
    if (window.innerWidth === lastW) return;   // height-only: the iOS toolbar, ignore
    lastW = window.innerWidth;
    soon();
  }, { passive: true });
  phone.addEventListener('change', soon);
  if (window.ResizeObserver) {
    const main = document.getElementById('MainContent');
    const ro = new ResizeObserver(soon);
    if (main) ro.observe(main);
    document.querySelectorAll('.shopify-section-group-footer-group').forEach((s) => ro.observe(s));
  }
  document.addEventListener('shopify:section:load', soon);
  if (document.readyState === 'complete') build();
  else window.addEventListener('load', build, { once: true });
})();

/* =========================================================
   INGREDIENT ANIMATIONS (vh-information's third column)
   Their loops only run while the panel is on screen.
   ========================================================= */
(() => {
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => e.target.classList.toggle('is-playing', e.isIntersecting));
  }, { rootMargin: '80px 0px' });
  VH.section('[data-vh-info-anim]', (el) => io.observe(el), (el) => io.unobserve(el));
})();
